import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Map, Clock, User, LogOut, AlertTriangle, QrCode, RefreshCw, Bell, Link, ChevronDown, ChevronUp, Gift, Search, UserCheck, Plus, Minus, Music, Globe, ShieldCheck, Mail, CheckCircle, XCircle, X, ClipboardList, Send, Image, Mic, Square, Megaphone, Crown, ArrowLeft, Users, Camera } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { findBestTeamByEmail } from '../lib/teamUtils';
import { useStore } from '../store/useStore';
import { MapViewMapbox } from './MapViewMapbox';
import { MapView } from './MapView';
import { GanttTimeline } from './GanttTimeline';
import { supabase } from '../lib/supabase';
import { Scanner } from '@yudiel/react-qr-scanner';
import { compressImage } from '../lib/imageCompression';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useTeamShiftEnricher } from '../hooks/useTeamShiftEnricher';
import { motion } from 'framer-motion';
import { PointIcon } from './icons/PointIcon';
import QRCode from 'react-qr-code';





export function TeamApp() {
  const [activeTab, setActiveTab] = useState<'map' | 'timeline' | 'badge' | 'swaps' | 'messages' | 'external_missions'>(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'badge') return 'badge';
    if (tabParam === 'timeline') return 'timeline';
    if (tabParam === 'messages') return 'messages';
    if (tabParam === 'external_missions') return 'external_missions';
    if (tabParam === 'swaps') return 'swaps';
    return 'map';
  });

  // Handle URL deep-linking on mount (e.g. ?tab=messages from push notification or email)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    
    if (tabParam) {
      const allowedTabs: ('map' | 'timeline' | 'badge' | 'swaps' | 'messages' | 'external_missions')[] = [
        'map', 'timeline', 'badge', 'swaps', 'messages', 'external_missions'
      ];
      if (allowedTabs.includes(tabParam as any)) {
        setActiveTab(tabParam as any);
      }
      
      // Clean up the URL query parameter gracefully without refreshing
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('tab');
      window.history.replaceState({}, document.title, newUrl.toString());
    }

    // Écouter les messages du Service Worker pour les clics sur notifications push en tâche de fond
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NAVIGATE') {
        try {
          const urlObj = new URL(event.data.url, window.location.origin);
          const tabParam = urlObj.searchParams.get('tab');
          const allowedTabs = ['map', 'timeline', 'badge', 'swaps', 'messages', 'external_missions'];
          if (tabParam && allowedTabs.includes(tabParam)) {
            setActiveTab(tabParam as any);
          }
        } catch (e) {
          console.error('[TeamApp] Error parsing SW navigation URL:', e);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  const {
    currentUser,
    team,
    points,
    shifts,
    updateTeam,
    updateShift,
    addIssue,
    categories,
    messages,
    markMessageRead,
    globalSettings,
    organizations,
    projects,
    impersonatedTeamId,
    offerProducts,
    customQuotas,
    consumptions,
    checkins,
    addTeamConsumption,
    addTeamCheckin,
    addTeamDirectMessages,
    createApprovalRequest,
    cancelRequest,
    relanceRequest,
    approvalRequests,
    orgAdminEmails,
    categoryManagerEmails,
    sharedTeamOrganizations,
    teamCustomFieldValues,
  } = useStore();

  // Helper: resolve effective quota for a team+product combo
  // Priority: per-team custom > category quota (embedded in product) > product default
  const getEffectiveQuota = (teamId: string, prodName: string, product: typeof offerProducts[0]) => {
    const custom = customQuotas.find(q => q.teamId === teamId && q.productName === prodName);
    if (custom) return custom.customQuota;
    // Check category quotas embedded in the product (reliable, survives sync)
    const catQuotas = product.categoryQuotas || [];
    if (catQuotas.length > 0) {
      const s = team.find(st => st.id === teamId);
      if (s?.categoryId) {
        const match = catQuotas.find(q => q.categoryId === s.categoryId);
        if (match) return match.quota;
      }
      return 0;
    }
    return product.defaultQuota;
  };

  const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
    const currentTeam = team.find(s => s.id === effectiveTeamId);
  // For shared team across multiple orgs, determine the "primary" org from the team record
  // but also make all orgs available for display
  const currentOrg = organizations.find(o => o.id === currentTeam?.orgId);
  // All orgs this team member belongs to: primary org + shifts orgs + shared links
  const teamOrgs = organizations.filter(o =>
    o.id === currentTeam?.orgId ||
    shifts.some(s => (s.teamId === currentTeam?.id || s.teamId === currentTeam?.parentArtistId) && s.orgId === o.id) ||
    (sharedTeamOrganizations || []).some(link => link.teamId === currentTeam?.id && link.orgId === o.id)
  );
  const appLogo = currentOrg?.logoUrl || globalSettings?.appLogoUrl || '/biplogo.svg';
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueMessage, setIssueMessage] = useState('');
  const [issuePointId, setIssuePointId] = useState('');
  const [issueType, setIssueType] = useState<'incident_public' | 'incident_internal'>('incident_public');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [walletScannerOpen, setWalletScannerOpen] = useState(false); // Link My Wallet scanner
  const [walletReplaceMode, setWalletReplaceMode] = useState(false); // true when replacing existing linked QR
  const [walletLinkSuccess, setWalletLinkSuccess] = useState(''); // success message after wallet link/unlink
  const [scanError, setScanError] = useState('');
  // ── New Message Composer States ──
  const [showNewMessage, setShowNewMessage] = useState(true);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [groupName, setGroupName] = useState('');
  const [convReply, setConvReply] = useState('');
  const [convSending, setConvSending] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [messageContent, setMessageContent] = useState('');
  const [messageImage, setMessageImage] = useState<string | null>(null);
  const [messageVoice, setMessageVoice] = useState<Blob | null>(null);
  const [isSelectAllCategory, setIsSelectAllCategory] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [recordingTeam, setRecordingTeam] = useState(false);
  const mediaRecorderRefTeam = useRef<MediaRecorder | null>(null);
  const fileInputRefTeam = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Distributor Console states
  const [distributorScannerOpen, setDistributorScannerOpen] = useState(false);
  const [scannedTeamId, setScannedTeamId] = useState<string | null>(null);
  const [distributorPointId, setDistributorPointId] = useState(() => {
    const orgPts = points.filter(p => p.orgId === currentTeam?.orgId);
    return orgPts[0]?.id || '';
  });
  const [basket, setBasket] = useState<Record<string, number>>({});
  const [distributorSearchQuery, setDistributorSearchQuery] = useState('');
  const [distributorMode, setDistributorMode] = useState<'consumption' | 'checkin'>('consumption');
  const [distributorScanError, setDistributorScanError] = useState('');
  const [distTabSub, setDistTabSub] = useState<'scan' | 'search' | 'list'>('scan');
  const [distributorLinkTarget, setDistributorLinkTarget] = useState<string | null>(null); // Team member to link wallet to (Flow B: List→Scan)
  const [distributorUnlinkedQr, setDistributorUnlinkedQr] = useState<string | null>(null);  // Scanned QR value not yet linked (Flow A: Scan→Link)
  const [distributorSuccessMsg, setDistributorSuccessMsg] = useState('');
  const [confirmAction, setConfirmAction] = useState<{message: string, onConfirm: () => void} | null>(null);
  const [isFocusBarOpen, setIsFocusBarOpen] = useState(true);

  // Auto-collapse focus bar in Messages tab
  useEffect(() => {
    if (activeTab === 'messages') setIsFocusBarOpen(false);
  }, [activeTab]);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Custom field completion overlay
  const [pendingCustomFieldDefs, setPendingCustomFieldDefs] = useState<any[]>([]);
  const [pendingCustomFieldValues, setPendingCustomFieldValues] = useState<Record<string, string>>({});
  const [pendingSubmitting, setPendingSubmitting] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);

  // Freelance / External Missions states
  const [tempEmail, setTempEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sentCode, setSentCode] = useState('');
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [rateInput, setRateInput] = useState(0);
  const [currencyInput, setCurrencyInput] = useState('TND');
  const [zoneInput, setZoneInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [customSkillInput, setCustomSkillInput] = useState('');
    const [negotiatingShiftId, setNegotiatingShiftId] = useState<string | null>(null);
    const [counterProposedAmount, setCounterProposedAmount] = useState<number>(0);

    useEffect(() => {
    if (currentTeam) {
      setTempEmail(currentTeam.email || '');
      setRateInput(currentTeam.indicativeDailyRate || 0);
      setZoneInput(currentTeam.geographicalZone || '');
      setDescriptionInput(currentTeam.publicDescription || '');
    }
  }, [effectiveTeamId]);
  const touchStartY = useRef(0);
  const isReadyToPull = useRef(false);
  const lastCleanup = useRef<(() => void) | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Store pullDistance in a ref to avoid re-binding listeners on every pixel of pull
  const pullDistanceRef = useRef(0);
  pullDistanceRef.current = pullDistance;

  const contentAreaRef = useCallback((node: HTMLDivElement | null) => {
    if (lastCleanup.current) {
      lastCleanup.current();
      lastCleanup.current = null;
    }

    if (!node) return;

    const getScrollParent = (n: HTMLElement | null): HTMLElement | null => {
      if (!n) return null;
      if (n === node) return null;
      const style = window.getComputedStyle(n);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll' || n.classList.contains('overflow-y-auto')) {
        return n;
      }
      return getScrollParent(n.parentElement);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (activeTab === 'map' || activeTab === 'messages' || isRefreshing) return;
      const scrollParent = getScrollParent(e.target as HTMLElement);
      const scrollTop = scrollParent ? scrollParent.scrollTop : 0;
      touchStartY.current = e.touches[0].clientY;
      isReadyToPull.current = scrollTop <= 1;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (activeTab === 'map' || activeTab === 'messages' || isRefreshing) return;
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - touchStartY.current;

      const scrollParent = getScrollParent(e.target as HTMLElement);
      const scrollTop = scrollParent ? scrollParent.scrollTop : 0;

      if (scrollTop <= 1) {
        if (!isReadyToPull.current && deltaY > 0) {
          touchStartY.current = currentY;
          isReadyToPull.current = true;
        }

        if (isReadyToPull.current && deltaY > 0) {
          if (e.cancelable) {
            e.preventDefault();
          }
          const resistanceDistance = Math.min(deltaY * 0.45, 120);
          setPullDistance(resistanceDistance);
        } else {
          setPullDistance(0);
        }
      } else {
        isReadyToPull.current = false;
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isReadyToPull.current) return;
      isReadyToPull.current = false;
      if (pullDistanceRef.current >= 55) {
        setIsRefreshing(true);
        setPullDistance(60);
        setTimeout(() => {
          window.location.reload();
        }, 900);
      } else {
        setPullDistance(0);
      }
    };

    node.addEventListener('touchstart', handleTouchStart, { passive: true });
    node.addEventListener('touchmove', handleTouchMove, { passive: false });
    node.addEventListener('touchend', handleTouchEnd, { passive: true });

    lastCleanup.current = () => {
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeTab, isRefreshing]);

  // FCM Deep Link parameter cleaning was consolidated into the on-mount deep-linking handler above

    // Push notifications FCM
  const { permissionState, fcmReady, fcmError, fcmStep, requestPermission } = usePushNotifications(currentTeam?.id);
  // Enrichir les shifts du team (récupère les shifts manquants des autres orgs)
  useTeamShiftEnricher();

  // Check PWA standalone mode and update database
  useEffect(() => {
    if (currentTeam && !currentTeam.pwaInstalled) {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as any).standalone === true;
      if (isStandalone) {
        updateTeam(currentTeam.id, { pwaInstalled: true });
        console.log("[PWA] Standalone mode detected, updating pwaInstalled in database.");
      }
    }
  }, [currentTeam?.id, currentTeam?.pwaInstalled]);

  // Check for missing required custom fields (DISABLED — caused React #300 crash on team login)
  // The async setState in this useEffect triggered a re-render during initial mount,
  // colliding with the admin→staff role transition. To be re-enabled with fix.
  useEffect(() => {
    // DISABLED
  }, []);

  const handleSaveCustomFields = async () => {
    setPendingSubmitting(true);
    setPendingError(null);

    // Validate required fields
    for (const field of pendingCustomFieldDefs) {
      if (field.required && !(pendingCustomFieldValues[field.id] || '').trim()) {
        setPendingError(`Le champ "${field.label}" est requis.`);
        setPendingSubmitting(false);
        return;
      }
    }

    try {
      const inserts = pendingCustomFieldDefs
        .filter((f: any) => pendingCustomFieldValues[f.id] !== undefined && pendingCustomFieldValues[f.id] !== '')
        .map((f: any) => ({
          id: uuidv4(),
          field_id: f.id,
          staff_id: currentTeam!.id,
          value: pendingCustomFieldValues[f.id] || null,
        }));

      if (inserts.length > 0) {
        const { error } = await supabase.from('staff_custom_field_values').insert(inserts);
        if (error) {
          setPendingError('Erreur lors de l\'enregistrement. Veuillez réessayer.');
          setPendingSubmitting(false);
          return;
        }
      }

      setPendingCustomFieldDefs([]);
      setPendingCustomFieldValues({});
    } catch (err) {
      console.error('[TeamApp] Error saving custom fields:', err);
      setPendingError('Erreur inattendue. Veuillez réessayer.');
    }
    setPendingSubmitting(false);
  };

  // ── Supabase Realtime + Polling for messages (all tabs) ──
  useEffect(() => {
    if (!currentTeam?.id) return;
    let mounted = true;
    const mapMsg = (row: any) => ({
      id: row.id, teamId: row.staff_id, orgId: row.org_id, projectId: row.project_id,
      content: row.content, timestamp: row.created_at, read: row.read ?? false,
      fromTeam: row.from_staff ?? false, senderName: row.sender_name ?? undefined,
      recipientTeamId: row.recipient_staff_id ?? undefined,
      imageUrl: row.image_url ?? null, voiceUrl: row.voice_url ?? null,
    });

    // Realtime: instant delivery regardless of active tab
    const channel = supabase
      .channel(`team-messages-${currentTeam.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload: any) => {
        if (!mounted) return;
        const row = payload.new;
        // Only accept messages for me (sent to me or from me)
        if (row.staff_id === currentTeam.id || row.recipient_staff_id === currentTeam.id) {
          useStore.setState((s) => {
            if ((s.messages || []).some(m => m.id === row.id)) return s;
            return { messages: [...(s.messages || []), mapMsg(row)] };
          });
        }
      })
      .subscribe();

    // Polling backup (30s — réduit de 5s pour économiser l'egress)
    let lastCheck = new Date().toISOString();
    const MSG_COLUMNS = 'id, staff_id, org_id, project_id, content, created_at, read, from_staff, sender_name, recipient_staff_id';
    const poll = async () => {
      if (!mounted || document.hidden) return;
      try {
        // Fetch both outgoing (staff_id = me) and incoming (recipient_staff_id = me)
        const [{ data: out }, { data: inc }] = await Promise.all([
          supabase.from('messages').select(MSG_COLUMNS).eq('staff_id', currentTeam.id).gt('created_at', lastCheck).is('deleted_at', null).order('created_at', { ascending: true }),
          supabase.from('messages').select(MSG_COLUMNS).eq('recipient_staff_id', currentTeam.id).gt('created_at', lastCheck).is('deleted_at', null).order('created_at', { ascending: true }),
        ]);
        const all = [...(out || []), ...(inc || [])];
        if (all.length > 0 && mounted) {
          const maxTs = all.reduce((max: string, row: any) => row.created_at > max ? row.created_at : max, lastCheck);
          if (maxTs > lastCheck) lastCheck = maxTs;
          useStore.setState((s) => {
            const existingIds = new Set((s.messages || []).map(m => m.id));
            const fresh = all.map(mapMsg).filter((m: any) => !existingIds.has(m.id));
            return fresh.length > 0 ? { messages: [...(s.messages || []), ...fresh] } : s;
          });
        }
      } catch (_) { /* ignore */ }
    };

    const interval = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(interval); supabase.removeChannel(channel); };
  }, [currentTeam?.id]);

  // Clean up expired approval requests for this team member
  useEffect(() => {
    if (!currentTeam?.id) return;
    const myPendingRequests = (approvalRequests || []).filter(
      r => r.teamId === currentTeam.id && r.status === 'pending' && new Date(r.expiresAt) <= new Date()
    );
    if (myPendingRequests.length > 0) {
      myPendingRequests.forEach(r => cancelRequest(r.id));
    }
  }, [approvalRequests, currentTeam?.id, cancelRequest]);

  const handleLogout = async () => {
    const confirmLogout = window.confirm("Êtes-vous sûr de vouloir vous déconnecter ?");
    if (!confirmLogout) return;

    localStorage.removeItem('wfm_staff_token');
    localStorage.removeItem('wfm_manager_token');
    localStorage.removeItem('wfm_org_token');
    localStorage.removeItem('wfm_current_org_id');
    localStorage.removeItem('wfm_device_id');
    localStorage.removeItem('wfm_user_email');
    
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  if (!currentTeam) {
    return <div className="p-4 text-white">Team not found</div>;
  }

  // Blocking overlay for missing required custom fields (DISABLED — see useEffect above)
  if (false) {
    return (
      <div className="fixed inset-0 bg-[#0A0B0E] z-[200] flex items-center justify-center p-4">
        <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl max-w-md w-full shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-[#2D3139] p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center border border-indigo-500/30">
                <ClipboardList className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Informations requises</h1>
                <p className="text-indigo-300 text-sm">Complétez votre profil pour continuer</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-slate-400 text-sm">
              Votre organisation requiert les informations suivantes. Veuillez les remplir pour accéder à l'application.
            </p>

            {pendingError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl">
                {pendingError}
              </div>
            )}

            <div className="space-y-4">
              {pendingCustomFieldDefs.map((field: any) => (
                <div key={field.id} className="w-full">
                  <label className="block text-xs font-semibold text-slate-400 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {field.field_type === 'textbox' && (
                    <input type="text"
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                      placeholder={field.label}
                      value={pendingCustomFieldValues[field.id] || ''}
                      onChange={e => setPendingCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                    />
                  )}
                  {field.field_type === 'number' && (
                    <input type="number"
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                      placeholder={field.label}
                      value={pendingCustomFieldValues[field.id] || ''}
                      onChange={e => setPendingCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                    />
                  )}
                  {field.field_type === 'date' && (
                    <input type="date"
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all"
                      value={pendingCustomFieldValues[field.id] || ''}
                      onChange={e => setPendingCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                    />
                  )}
                  {field.field_type === 'radio' && (
                    <div className="grid grid-cols-2 gap-2 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]">
                      {(field.options || []).map((opt: string) => (
                        <label key={opt}
                          className={`flex items-center justify-center p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                            pendingCustomFieldValues[field.id] === opt
                              ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400'
                              : 'border-[#2D3139] text-slate-400 hover:border-slate-600'
                          }`}
                        >
                          <input type="radio"
                            name={`pcf_${field.id}`}
                            value={opt}
                            checked={pendingCustomFieldValues[field.id] === opt}
                            onChange={e => setPendingCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                            className="sr-only"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}
                  {field.field_type === 'checkbox' && (
                    <label className="flex items-center gap-3 p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139] cursor-pointer hover:border-slate-700 transition-all">
                      <input type="checkbox"
                        checked={pendingCustomFieldValues[field.id] === 'true'}
                        onChange={e => setPendingCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? 'true' : 'false' }))}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-indigo-500 focus:ring-indigo-500/20"
                      />
                      <span className="text-sm text-slate-400">{field.label}</span>
                    </label>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={handleSaveCustomFields}
              disabled={pendingSubmitting}
              className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 mt-4"
            >
              {pendingSubmitting ? (
                <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Enregistrement...</>
              ) : (
                'Enregistrer et continuer'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

    // Include shifts from ALL orgs (shared team may have shifts in multiple orgs)
  // For artist group sub-members: also include parent artist's shifts
  const teamShifts = shifts.filter(s => s.teamId === currentTeam.id || (currentTeam.parentArtistId && s.teamId === currentTeam.parentArtistId));
  const now = new Date();
  const currentOrNextShifts = teamShifts.filter(s => new Date(s.end) > now).sort((a,b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const currentShift = currentOrNextShifts[0];

  // Swap and Unavailability are allowed only on points where enabled
  const swapAllowedOnAnyShift = currentOrNextShifts.some(s => {
    const point = points.find(p => p.id === s.pointId);
    return point?.swapEnabled === true;
  });

  const unavailabilityAllowedOnAnyShift = currentOrNextShifts.some(s => {
    const point = points.find(p => p.id === s.pointId);
    return point?.unavailabilityEnabled === true;
  });

  const swapableShifts = shifts.filter(s => {
    if (!s.swapRequested || s.teamId === currentTeam.id || new Date(s.start) <= now) return false;
    const point = points.find(p => p.id === s.pointId);
    // Only show swappable shifts at points where swapEnabled is on
    if (!point?.swapEnabled) return false;
    const cat = categories.find(c => c.id === point?.categoryId);
    return cat && !cat.isGeneral;
  });

  // Include both outgoing (teamId = me) AND incoming (recipientTeamId = me) messages
  const myMessages = (messages?.filter(m =>
    m.teamId === currentTeam.id || m.recipientTeamId === currentTeam.id
  ) || []).sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  // Unread: exclude only messages I sent myself (fromTeam + my name)
  const myName = currentTeam?.name;
  const unreadMessages = myMessages.filter(m => !m.read && !(m.fromTeam && m.senderName === myName));

  // ── Contact Groups for New Message ──
  // All orgs this team belongs to (primary + shared)
  const myOrgIds = teamOrgs.map(o => o.id);

  // Custom field values lookup for search
  const cfValuesByTeam = useMemo(() => {
    const lookup: Record<string, string> = {};
    teamCustomFieldValues.forEach(v => {
      if (!lookup[v.teamId]) lookup[v.teamId] = '';
      lookup[v.teamId] += ' ' + (v.value || '').toLowerCase();
    });
    return lookup;
  }, [teamCustomFieldValues]);

  // Team in same categories across the member's orgs
  // Use categoryIds array if it has entries, otherwise fall back to single categoryId
  const myCategoryIds = new Set(
    (currentTeam?.categoryIds?.length || 0) > 0
      ? currentTeam!.categoryIds!
      : (currentTeam?.categoryId ? [currentTeam.categoryId] : [])
  );
  // Also include categories where I'm a manager (manager can message their managed team)
  if (currentTeam?.email) {
    const myManagedCategories = categoryManagerEmails
      .filter(m => m.email?.toLowerCase().trim() === currentTeam.email?.toLowerCase().trim())
      .map(m => m.categoryId);
    myManagedCategories.forEach(cid => myCategoryIds.add(cid));
  }
  // ── Artists: only see their assigned team member ──
  // Artists have isArtist=true, parentArtistId, OR assignedTeamId set
  const isArtistUser = currentTeam?.isArtist || !!currentTeam?.parentArtistId || !!currentTeam?.assignedTeamId || false;
  const assignedTeamMember = currentTeam?.assignedTeamId
    ? team.find(s => s.id === currentTeam.assignedTeamId)
    : null;
  // Artists: only see assigned team member (or empty if none assigned)
  const categoryTeam = isArtistUser
    ? (assignedTeamMember ? [assignedTeamMember] : [])
    : team.filter(s =>
        myOrgIds.includes(s.orgId || '') &&
        s.id !== currentTeam?.id &&
        !s.isArtist &&
        myCategoryIds.size > 0 &&
        ((s.categoryId && myCategoryIds.has(s.categoryId)) || (s.categoryIds || []).some(cid => myCategoryIds.has(cid)))
    );

  const allCategoryCount = categoryTeam.length;

  // Helper to get org name for a team member
  const getOrgName = (orgId?: string | null) => {
    if (!orgId) return '';
    const org = organizations.find(o => o.id === orgId);
    return org ? ` · ${org.name}` : '';
  };

  // Manager contacts: from category_managers table (confirmed emails), resolve org via category
  // Exclude self (for team members who are also managers)
  const managerContacts = categoryManagerEmails
    .map(cm => {
      const cat = categories.find(c => c.id === cm.categoryId);
      if (!cat || !myOrgIds.includes(cat.orgId)) return null;
      // Exclude if this manager is the current team member
      if (currentTeam?.email && cm.email?.toLowerCase().trim() === currentTeam.email.toLowerCase().trim()) return null;
      return {
        id: `manager-${cm.categoryId}`,
        name: `Manager: ${cat.name}`,
        role: 'manager' as const,
        categoryId: cm.categoryId,
        email: cm.email,
        orgName: organizations.find(o => o.id === cat.orgId)?.name || '',
      };
    })
    .filter(Boolean) as { id: string; name: string; role: 'manager'; categoryId: string; email: string; orgName: string }[];

  // Admin contacts: from organization_admins table (confirmed emails), filtered by org
  // Exclude self (admin whose email matches the current team member)
  const adminContacts = orgAdminEmails
    .filter(a => myOrgIds.includes(a.orgId))
    .filter(a => !currentTeam?.email || a.email?.toLowerCase().trim() !== currentTeam.email.toLowerCase().trim())
    .map(a => ({
      id: `admin-${a.orgId}-${a.email}`,
      name: `Admin${orgAdminEmails.filter(x => x.orgId === a.orgId).length > 1 ? ` (${a.email.split('@')[0]})` : ''}`,
      role: 'admin' as const,
      email: a.email,
    }));
  // All selectable contacts combined (with email for dedup)
  type ContactEntry = { id: string; name: string; role: 'staff' | 'manager' | 'admin'; photo?: string; categoryId?: string; orgName: string; email: string; secondaryRoles: string[] };
  const staffContacts: ContactEntry[] = categoryTeam.map(s => ({ id: s.id, name: s.name, role: 'staff', photo: s.photo, categoryId: s.categoryId, orgName: getOrgName(s.orgId), email: s.email || '', secondaryRoles: [] }));
  const mgrContacts: ContactEntry[] = managerContacts.map(m => ({ id: m.id, name: m.name, role: m.role, photo: undefined, categoryId: m.categoryId, orgName: m.orgName ? ` · ${m.orgName}` : '', email: m.email, secondaryRoles: [] }));
  const admContacts: ContactEntry[] = adminContacts.map(a => ({ id: a.id, name: a.name, role: a.role, photo: undefined, categoryId: undefined, orgName: '', email: a.email, secondaryRoles: [] }));
  // Helper: build role label from a contact
  const roleLabel = (c: { role: string; name: string }) => {
    if (c.role === 'staff') return 'Team';
    if (c.role === 'admin') return 'Admin';
    // Manager: extract category from name "Manager: CategoryName"
    const cat = c.name.replace(/^Manager:\s*/, '');
    return `Manager: ${cat}`;
  };
  // Dedup by email: same person = single unified contact, enriched with all roles
  const allContacts: ContactEntry[] = (() => {
    const seen: Record<string, ContactEntry> = {};
    // Staff first (highest priority — has direct team ID + photo)
    for (const c of staffContacts) {
      const key = c.email?.toLowerCase().trim();
      if (key) seen[key] = { ...c };
      else seen[c.id] = { ...c };
    }
    // Manager: add if email not seen, otherwise enrich with secondary role
    for (const c of mgrContacts) {
      const key = c.email?.toLowerCase().trim();
      if (key) {
        if (!seen[key]) {
          seen[key] = { ...c };
        } else {
          seen[key].secondaryRoles.push(roleLabel(c));
        }
      } else {
        if (!seen[c.id]) seen[c.id] = { ...c };
      }
    }
    // Admin: add if email not seen, otherwise enrich
    for (const c of admContacts) {
      const key = c.email?.toLowerCase().trim();
      if (key) {
        if (!seen[key]) {
          seen[key] = { ...c };
        } else {
          seen[key].secondaryRoles.push(roleLabel(c));
        }
      } else {
        if (!seen[c.id]) seen[c.id] = { ...c };
      }
    }
    return Object.values(seen);
  })();

  // Conversation with selected contact — search all lists + deduped
  const selectedConversation = selectedConversationId
    ? (allContacts.find(c => c.id === selectedConversationId)
       || staffContacts.find(c => c.id === selectedConversationId)
       || mgrContacts.find(c => c.id === selectedConversationId)
       || admContacts.find(c => c.id === selectedConversationId))
    : null;
  // Resolve the selected contact's team ID for outgoing-message matching
  const selectedContactTeamId = selectedConversation
    ? (selectedConversation.role === 'staff'
        ? selectedConversation.id // staff contact ID IS the team ID
        : findBestTeamByEmail(team, selectedConversation.email)?.id) // resolve manager/admin email → team ID
    : null;
  // Build name patterns to match messages from this contact (same person, different roles)
  const contactNamePatterns: string[] = (() => {
    if (!selectedConversation) return [];
    const norm = (s: string) => s.replace(/\s*:\s*/g, ':').toLowerCase().trim();
    const patterns: string[] = [];
    const email = selectedConversation.email;
    const realTeamMember = email ? findBestTeamByEmail(team, email) : null;
    const realName = realTeamMember?.name || '';
    const emailPrefix = email?.split('@')[0]?.toLowerCase() || '';

    patterns.push(norm(selectedConversation.name));
    if (realName) {
      patterns.push(norm(realName));
      patterns.push(norm(`Admin: ${realName}`));   // normalized (no space before colon)
      patterns.push(norm(`Manager: ${realName}`));
    }
    if (emailPrefix) patterns.push(emailPrefix);
    for (const sr of selectedConversation.secondaryRoles || []) {
      // Only include specific roles (with colon), skip generic single-word roles like "Admin"
      if (sr.includes(':')) patterns.push(norm(sr));
    }
    return [...new Set(patterns)]; // dedup
  })();

  const conversationMessages = selectedConversation
    ? messages
        .filter(m => {
          const sNorm = (m.senderName || '').replace(/\s*:\s*/g, ':').toLowerCase().trim();
          // Message FROM selected contact TO me (incoming) — exclude only MY own outgoing
          if ((m.teamId === currentTeam.id || m.recipientTeamId === currentTeam.id) && !(m.fromTeam && m.senderName === currentTeam?.name)) {
            if (contactNamePatterns.some(p => sNorm.includes(p) || p.includes(sNorm))) return true;
          }
          // Message sent BY me (as admin) TO selected contact — senderName matches my team name
          if (!m.fromTeam && m.teamId === selectedContactTeamId && currentTeam?.name && sNorm.includes(currentTeam.name.toLowerCase().trim())) return true;
          // Message sent BY me (as team) TO selected contact — must match the contact's team ID
          if (m.fromTeam && m.senderName === currentTeam.name && selectedContactTeamId && m.recipientTeamId === selectedContactTeamId) return true;
          return false;
        })
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    : [];

  // Auto-scroll chat to bottom when conversation or messages change
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [conversationMessages, selectedConversationId]);

  // Helper: unread count from a contact (include incoming, exclude my own outgoing)
  const getUnreadFrom = (contact: { name: string; email?: string }): number => {
    const norm = (s: string) => s.replace(/\s*:\s*/g, ':').toLowerCase().trim();
    const cName = norm(contact.name);
    const cEmail = contact.email?.toLowerCase().trim() || '';
    const cEmailPrefix = cEmail.split('@')[0] || '';
    // Look up real name from team record (if any) for better matching
    const realTeamMember = contact.email ? findBestTeamByEmail(team, contact.email) : null;
    const realName = norm(realTeamMember?.name || '');
    // Build a list of patterns to match against the sender name (filter empty strings!)
    const myPatterns: string[] = [cName, cEmailPrefix].filter(Boolean);
    if (realName) myPatterns.push(realName, `admin:${realName}`, `manager:${realName}`);
    // Extract name from parentheses in admin contacts like "Admin (emailprefix)"
    const parenMatch = cName.match(/\(([^)]+)\)/);
    if (parenMatch) myPatterns.push(parenMatch[1]);
    // Also try the contact's secondary roles (from allContacts lookup)
    const enriched = cEmail ? allContacts.find(c => c.email === cEmail) : null;
    if (enriched) {
      for (const sr of enriched.secondaryRoles) { if (sr.includes(':')) myPatterns.push(norm(sr)); }
    }

    return messages.filter(m => {
      if ((m.teamId !== currentTeam.id && m.recipientTeamId !== currentTeam.id) || m.read) return false;
      if (m.fromTeam && m.senderName === myName) return false;
      const sName = norm(m.senderName || '');
      return myPatterns.some(p => sName.includes(p) || p.includes(sName));
    }).length;
  };

  // Auto-mark messages as read when opening conversation
  const openConversation = (contact: { name: string; id: string; email?: string }) => {
    setSelectedConversationId(selectedConversationId === contact.id ? null : contact.id);
    setSelectedContactIds([]);
    // Mark ALL unread messages for this team member as read (simplifies matching issues)
    const unreadIds = messages
      .filter(m => {
        if (m.read) return false;
        // Skip my own outgoing messages
        if (m.fromTeam && m.senderName === currentTeam?.name) return false;
        // Must be for me (incoming or outgoing on my teamId)
        return m.teamId === currentTeam.id || m.recipientTeamId === currentTeam.id;
      })
      .map(m => m.id);
    unreadIds.forEach(id => markMessageRead(id));
  };

  // Resolve display name for a contact
  const getContactDisplay = (contactId: string): string => {
    const c = allContacts.find(x => x.id === contactId);
    return c?.name || contactId;
  };

  // Image picker handler (reuses compressImage from MessagesView)
  const handleTeamImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, 400, 400, 0.5);
    setMessageImage(compressed);
  };

  // Voice recording handlers
  const startTeamRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRefTeam.current = recorder;
      const chunks: Blob[] = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = () => {
        setMessageVoice(new Blob(chunks, { type: 'audio/webm' }));
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      setRecordingTeam(true);
    } catch (err) {
      console.error('Microphone error:', err);
      alert('Impossible d\'accéder au microphone.');
    }
  };

  // Send direct reply in conversation (text, image, or voice)
  const handleConvSend = async () => {
    if ((!convReply.trim() && !messageImage && !messageVoice) || !selectedConversation) return;
    setConvSending(true);
    // Resolve the team ID for the selected contact
    let targetTeamId = '';
    if (selectedConversation.role === 'staff') {
      targetTeamId = selectedConversation.id;
    } else if (selectedConversation.role === 'manager') {
      const mc = managerContacts.find(m => m.id === selectedConversation.id);
      if (mc?.email) {
        const sr = findBestTeamByEmail(team, mc.email);
        if (sr) targetTeamId = sr.id;
      }
    } else if (selectedConversation.role === 'admin') {
      const ac = adminContacts.find(a => a.id === selectedConversation.id);
      if (ac?.email) {
        const sr = findBestTeamByEmail(team, ac.email);
        if (sr) targetTeamId = sr.id;
      }
    }
    // Process voice to base64 if present
    let voiceDataUrl: string | null = null;
    if (messageVoice) {
      voiceDataUrl = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(messageVoice);
      });
    }
    const finalContent = convReply.trim() || (messageVoice ? '🎤 Message vocal' : '📷 Image');
    const target = targetTeamId || `admin-${selectedConversation.id}`;
    addTeamDirectMessages([target], finalContent, currentTeam?.name || 'Team', currentTeam.id, messageImage, voiceDataUrl);
    setConvReply('');
    setMessageImage(null);
    setMessageVoice(null);
    setConvSending(false);
  };

  const stopTeamRecording = () => {
    mediaRecorderRefTeam.current?.stop();
    setRecordingTeam(false);
  };

  // Send message logic
  const handleSendNewMessage = async () => {
    if (selectedContactIds.length === 0) return;
    const hasContent = messageContent.trim();
    const hasImage = !!messageImage;
    const hasVoice = !!messageVoice;
    if (!hasContent && !hasImage && !hasVoice) return;

    setSendingMessage(true);

    // Determine recipient team IDs for direct messages (from team contacts only)
    const directTeamIds = selectedContactIds.filter(id =>
      categoryTeam.some(s => s.id === id)
    );

    // For managers/admins, resolve their team record IDs if they exist
    const managerTeamIds: string[] = [];
    const adminTeamIds: string[] = [];
    for (const id of selectedContactIds) {
      if (managerContacts.some(m => m.id === id)) {
        const mc = managerContacts.find(m => m.id === id);
        if (mc?.email) {
          const teamRecord = findBestTeamByEmail(team, mc.email);
          if (teamRecord) managerTeamIds.push(teamRecord.id);
        }
      }
      if (adminContacts.some(a => a.id === id)) {
        const ac = adminContacts.find(a => a.id === id);
        if (ac?.email) {
          const teamRecord = findBestTeamByEmail(team, ac.email);
          if (teamRecord) adminTeamIds.push(teamRecord.id);
        }
      }
    }

    const allTargetTeamIds = [...directTeamIds, ...managerTeamIds, ...adminTeamIds];

    if (allTargetTeamIds.length === 0) {
      alert('Aucun destinataire valide trouvé.');
      setSendingMessage(false);
      return;
    }

    const processVoice = async (): Promise<string | null> => {
      if (!messageVoice) return null;
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(messageVoice);
      });
    };

    const voiceDataUrl = await processVoice();
    const finalContent = messageContent.trim() || (messageVoice ? '🎤 Message vocal' : '📷 Image');
    const senderName = selectedContactIds.length > 1 && groupName.trim()
      ? `${currentTeam?.name || 'Team'} (${groupName.trim()})`
      : currentTeam?.name || 'Team';

    if (isSelectAllCategory && directTeamIds.length === allCategoryCount) {
      // Broadcast to entire category → needs validation
      const allRecipientIds = allTargetTeamIds;
      const allRecipientNames = allRecipientIds.map(id => getContactDisplay(id));

      createApprovalRequest({
        teamId: currentTeam.id,
        teamName: senderName,
        categoryId: currentTeam.categoryId,
        orgId: currentTeam.orgId || undefined,
        projectId: undefined,
        content: finalContent,
        imageUrl: messageImage,
        voiceUrl: voiceDataUrl,
        recipientIds: allRecipientIds,
        recipientNames: allRecipientNames,
      });
      setShowValidationModal(true);
    } else {
      // Direct send to selected individuals
      addTeamDirectMessages(
        allTargetTeamIds,
        finalContent,
        senderName,
        currentTeam.id,
        messageImage,
        voiceDataUrl
      );
    }

    // Reset composer
    setMessageContent('');
    setMessageImage(null);
    setMessageVoice(null);
    setSelectedContactIds([]);
    setGroupName('');
    setIsSelectAllCategory(false);
    setSendingMessage(false);
  };


  const handleCheckOut = () => {
    if (currentShift) {
       updateShift(currentShift.id, { actualEnd: new Date().toISOString() });
    }
    updateTeam(currentTeam.id, { isOnDuty: false });
  };

  const submitIssue = () => {
    if (!issueMessage.trim() || !issuePointId) return;
    addIssue({
      pointId: issuePointId,
      teamId: currentTeam.id,
      teamName: currentTeam.name,
      message: issueMessage.trim(),
      type: issueType
    });
    setIssueMessage('');
    setIssuePointId('');
    setIssueType('incident_public');
    setIssueModalOpen(false);
  };

  const handleScan = (text: string) => {
    if (!currentShift) return;
    if (text === `myapp://checkin/${currentShift.pointId}`) {
      setScannerOpen(false);
      setScanError('');
      updateShift(currentShift.id, { actualStart: new Date().toISOString() });
      updateTeam(currentTeam.id, { isOnDuty: true });
    } else {
      setScanError("QR Code non valide pour ce point de service.");
    }
  };

  const handleDistributorScan = (text: string) => {
    // If in wallet-linking mode (Flow B: List→Scan), link the scanned QR to the target member
    if (distributorLinkTarget) {
      const targetMember = team.find(s => s.id === distributorLinkTarget);
      if (targetMember) {
        const qrValue = text.trim();
        // Check if this QR is already linked to another member
        const alreadyLinked = team.find(s => s.linkedWalletQr === qrValue && s.id !== distributorLinkTarget);
        if (alreadyLinked) {
          setDistributorScanError(`Ce QR est déjà lié à ${alreadyLinked.name}.`);
          return;
        }
        if (targetMember.linkedWalletQr && targetMember.linkedWalletQr !== qrValue) {
          if (!confirm(`${targetMember.name} a déjà un QR lié. Voulez-vous le remplacer ?`)) return;
        }
        updateTeam(distributorLinkTarget, { linkedWalletQr: qrValue });
        setDistributorSuccessMsg(`Bracelet lié avec succès à ${targetMember.name} !`);
        setDistributorLinkTarget(null);
        setDistributorScanError('');
        setDistTabSub('list');
        return;
      }
    }

    // First try by team ID, then by linked wallet QR
    let foundTeam = team.find(s => s.id === text && s.orgId === currentTeam?.orgId);
    if (!foundTeam) {
      foundTeam = team.find(s => s.linkedWalletQr === text && s.orgId === currentTeam?.orgId);
    }
    if (foundTeam) {
      setScannedTeamId(foundTeam.id);
      setBasket({});
      setDistributorScanError('');
      setDistributorUnlinkedQr(null);
    } else if (currentTeam?.distAllowLinkWallet) {
      // QR not recognized — offer to link it to a member (Flow A: Scan→Link)
      setDistributorUnlinkedQr(text.trim());
      setDistributorScanError('');
    } else {
      setDistributorScanError("ID Team non reconnu pour cette organisation.");
    }
  };

  const handleLinkWalletToMember = (memberId: string) => {
    if (!distributorUnlinkedQr) return;
    const targetMember = team.find(s => s.id === memberId);
    if (!targetMember) return;
    // Check if already linked to someone else
    const alreadyLinked = team.find(s => s.linkedWalletQr === distributorUnlinkedQr && s.id !== memberId);
    if (alreadyLinked) {
      alert(`Ce QR est déjà lié à ${alreadyLinked.name}.`);
      return;
    }
    if (targetMember.linkedWalletQr && targetMember.linkedWalletQr !== distributorUnlinkedQr) {
      if (!confirm(`${targetMember.name} a déjà un QR lié. Voulez-vous le remplacer ?`)) return;
    }
    updateTeam(memberId, { linkedWalletQr: distributorUnlinkedQr });
    setDistributorSuccessMsg(`Bracelet lié avec succès à ${targetMember.name} !`);
    setDistributorUnlinkedQr(null);
    setScannedTeamId(memberId);
    setBasket({});
  };

  const handleCancelLinkWallet = () => {
    setDistributorUnlinkedQr(null);
    setDistributorLinkTarget(null);
    setDistributorScanError('');
    setDistributorSuccessMsg('');
  };

  // ─── Wallet QR link / unlink / replace ──────────────────────────────────

  const handleWalletScan = (text: string) => {
    if (!text || !text.trim()) return;
    const qrValue = text.trim();

    // Check if this QR is already linked to another team member
    const alreadyLinkedTo = team.find(s => s.linkedWalletQr === qrValue && s.id !== currentTeam.id);
    if (alreadyLinkedTo) {
      setWalletScannerOpen(false);
      setWalletReplaceMode(false);
      setWalletLinkSuccess(''); // clear any previous success
      alert(`Ce QR code est déjà lié à ${alreadyLinkedTo.name}. Il ne peut pas être lié à un autre membre.`);
      return;
    }

    if (currentTeam.linkedWalletQr && !walletReplaceMode) {
      // Already linked and not in replace mode — shouldn't happen, but safety
      setWalletScannerOpen(false);
      return;
    }

    if (walletReplaceMode) {
      // Replacing: confirm before overwriting
      if (confirm('Voulez-vous supprimer l\'ancien QR code lié et le remplacer par le nouveau ?')) {
        updateTeam(currentTeam.id, { linkedWalletQr: qrValue });
        setWalletLinkSuccess('Bracelet remplacé avec succès !');
      }
      setWalletReplaceMode(false);
    } else {
      // First time linking
      updateTeam(currentTeam.id, { linkedWalletQr: qrValue });
      setWalletLinkSuccess('Bracelet lié avec succès !');
    }
    setWalletScannerOpen(false);
  };

  const handleUnlinkWallet = () => {
    if (confirm('Voulez-vous dissocier ce QR code de votre compte ?')) {
      updateTeam(currentTeam.id, { linkedWalletQr: null });
      setWalletLinkSuccess('Bracelet dissocié avec succès.');
    }
  };

  const handleReplaceWallet = () => {
    setWalletReplaceMode(true);
    setWalletScannerOpen(true);
  };

  const handleLinkWallet = () => {
    setWalletReplaceMode(false);
    setWalletScannerOpen(true);
  };

  const handleConfirmDistribution = () => {
    if (!scannedTeamId) return;
    const items = Object.entries(basket).filter(([_, qty]) => qty > 0);
    if (items.length === 0) {
      alert("Veuillez sélectionner au moins un produit !");
      return;
    }

    let quotaExceeded = false;
    let exceededProducts: string[] = [];

    const lastResetDate = currentOrg?.settings && (currentOrg.settings as any).last_quota_reset
      ? new Date((currentOrg.settings as any).last_quota_reset)
      : null;

    items.forEach(([prodName, qty]) => {
      const prod = offerProducts.find(p => p.name === prodName && myOrgIds.includes(p.orgId || ''));
      if (!prod) return;

      const totalQuota = getEffectiveQuota(scannedTeamId, prodName, prod);

      const sumConsumed = consumptions
        .filter(c => c.teamId === scannedTeamId && c.productName === prodName && (!lastResetDate || new Date(c.createdAt) >= lastResetDate))
        .reduce((sum, c) => sum + c.quantity, 0);

      const remaining = Math.max(0, totalQuota - sumConsumed);
      if (qty > remaining) {
        quotaExceeded = true;
        exceededProducts.push(prodName);
      }
    });

    const executeInsert = () => {
      items.forEach(([prodName, qty]) => {
        const prod = offerProducts.find(p => p.name === prodName);
        if (!prod) return;
        addTeamConsumption({
          teamId: scannedTeamId,
          productName: prodName,
          quantity: qty,
          pointsValue: prod.pointsValue,
          scannedBy: currentTeam.id,
          pointId: distributorPointId || undefined
        });
      });

      alert("Commande validée avec succès !");
      setScannedTeamId(null);
      setBasket({});
      setDistributorScannerOpen(false);
    };

    if (quotaExceeded) {
      alert(`⛔ Quota épuisé pour : ${exceededProducts.join(', ')}. Attribution impossible.`);
    } else {
      executeInsert();
    }
  };

  const handleConfirmCheckin = () => {
    if (!scannedTeamId || !distributorPointId) return;
    addTeamCheckin({
      teamId: scannedTeamId,
      pointId: distributorPointId,
      scannedBy: currentTeam.id
    });
    alert("Présence (Check-in) validée avec succès !");
    setScannedTeamId(null);
    setDistributorScannerOpen(false);
  };

  const sendVerificationCode = async (email: string, code: string) => {
    try {
      const name = currentTeam?.name || email.split('@')[0];
      const { error } = await supabase.functions.invoke('send-email-verification', {
        body: { email, code, name }
      });
      if (error) {
        console.error('[EmailVerif] Erreur envoi:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('[EmailVerif] Exception:', err?.message);
      return false;
    }
  };

  const handleSendVerificationCode = async () => {
    if (!tempEmail || !tempEmail.includes('@')) {
      alert("Veuillez saisir une adresse mail valide.");
      return;
    }
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setSentCode(code);
    setCodeSent(true);
    setVerificationError('');

    // 1. Rechercher un team existant AVEC cet email (local store d'abord, puis DB)
    const cleanEmail = tempEmail.toLowerCase().trim();
    let targetTeamId: string | null = null;

    // Check local store first
    const localMatch = useStore.getState().team.find(
      s => s.email?.toLowerCase().trim() === cleanEmail
    );
    if (localMatch) {
      targetTeamId = localMatch.id;
    } else {
      // Query DB for ANY existing staff with this email
      const { data: found } = await supabase
        .from('staff')
        .select('id')
        .eq('email', cleanEmail)
        .is('deleted_at', null)
        .order('email_confirmed', { ascending: false }) // prefer confirmed
        .limit(1)
        .maybeSingle();
      if (found?.id) {
        targetTeamId = found.id;
      }
    }

    // 2. Sauvegarder ou mettre à jour
    if (targetTeamId) {
      updateTeam(targetTeamId, {
        email: cleanEmail, // ensure email is set
        verificationCode: code,
        verificationCodeSentAt: new Date().toISOString()
      });
    } else {
      // Aucun team n'existe avec cet email : on crée un profil autonome
      const orgId = currentTeam?.orgId || organizations[0]?.id || '8903636f-6fd0-471b-be5f-2595c43dd75a';
      await supabase.from('staff').insert({
        name: tempEmail.split('@')[0],
        email: cleanEmail,
        verification_code: code,
        verification_code_sent_at: new Date().toISOString(),
        is_profile_visible: false,
        email_confirmed: false,
        org_id: orgId
      });
    }

    await sendVerificationCode(tempEmail, code);
    alert(`Un code de validation à 6 chiffres a été envoyé à l'adresse ${tempEmail}.`);
  };

  const handleVerifyCode = async () => {
    let targetTeamId = currentTeam?.id;
    let dbVerificationCode = currentTeam?.verificationCode;

    if (!targetTeamId) {
      const { data: found } = await supabase
        .from('staff')
        .select('id, verification_code')
        .eq('email', tempEmail)
        .is('deleted_at', null)
        .maybeSingle();
      if (found) {
        targetTeamId = found.id;
        dbVerificationCode = found.verification_code;
      }
    } else {
      // Force fetch the latest code from the database to avoid store sync latency
      const { data: found } = await supabase
        .from('staff')
        .select('verification_code')
        .eq('id', targetTeamId)
        .maybeSingle();
      if (found) {
        dbVerificationCode = found.verification_code;
      }
    }

    if (!targetTeamId) {
      setVerificationError("Profil introuvable. Veuillez recommencer.");
      return;
    }

    if (verificationCodeInput === sentCode || (dbVerificationCode && verificationCodeInput === dbVerificationCode)) {
      updateTeam(targetTeamId, {
        email: tempEmail,
        emailConfirmed: true,
        isProfileVisible: true,
        verificationCode: null, // Clear code on success
        verificationCodeSentAt: null
      });
      alert("Félicitations ! Votre e-mail a été validé et votre profil est maintenant visible par les autres organisations. 🎉");
      setVerificationCodeInput('');
      setVerificationError('');
      setCodeSent(false);
    } else {
      setVerificationError("Code de confirmation invalide. Veuillez réessayer.");
    }
  };

  const toggleSkill = (skill: string) => {
    if (!currentTeam) return;
    const skills = currentTeam.publicSkills || [];
    if (skills.includes(skill)) {
      updateTeam(currentTeam.id, { publicSkills: skills.filter(s => s !== skill) });
    } else {
      updateTeam(currentTeam.id, { publicSkills: [...skills, skill] });
    }
  };

  const handleAddCustomSkill = () => {
    if (!currentTeam || !customSkillInput.trim()) return;
    const skills = currentTeam.publicSkills || [];
    if (!skills.includes(customSkillInput.trim())) {
      updateTeam(currentTeam.id, { publicSkills: [...skills, customSkillInput.trim()] });
    }
    setCustomSkillInput('');
  };

  const handleSaveProfile = () => {
    if (!currentTeam) return;
    updateTeam(currentTeam.id, {
      indicativeDailyRate: rateInput,
      proposedCurrency: currencyInput,
      geographicalZone: zoneInput,
      publicDescription: descriptionInput
    });
    alert("Profil freelance mis à jour avec succès ! ✨");
  };

  return (
    <div className={`flex flex-col relative ${impersonatedTeamId ? 'h-[calc(100dvh-2.5rem)]' : 'h-[100dvh]'} w-full bg-[#0A0B0E] text-white overflow-hidden`}>
      {currentTeam && !currentTeam.emailConfirmed && (
        <EmailVerificationBanner currentTeam={currentTeam} updateTeam={updateTeam} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-[#14161B]/80 backdrop-blur-md border-b border-[#2D3139]/50 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          {/* App Branding (Logo or Text) */}
          <div className="flex-shrink-0">
            {appLogo ? (
              <img src={appLogo} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain" />
            ) : (
              <div className="flex flex-col">
                <span className="text-base font-black text-white tracking-tight leading-none" style={{ fontFamily: '"Fredoka", sans-serif' }}>
                  Bip <span className="text-indigo-400">Teams</span>
                </span>
              </div>
            )}
          </div>
          
          {/* Divider */}
          <div className="w-px h-8 bg-gradient-to-b from-transparent via-[#2D3139] to-transparent" />

          {/* Team identity */}
          <div className="min-w-0">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest block mb-0.5">
              {currentTeam.isArtist
                ? (currentTeam.parentArtistId ? `Artiste 🎨 · Membre ${currentTeam.memberIndex}` : "Artiste 🎨")
                : (currentTeam.parentArtistId ? `Groupe · Membre ${currentTeam.memberIndex}` : "Membre Team")}
            </span>
            <span className="font-bold text-white text-sm block leading-tight truncate max-w-[180px]">
              {(() => {
                if (currentTeam.parentArtistId) {
                  const parent = team.find(t => t.id === currentTeam.parentArtistId);
                  return parent ? parent.name : currentTeam.name;
                }
                return currentTeam.name;
              })()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Bouton Distributeur / Check-in */}
          {(currentTeam?.isDistributor || currentTeam?.distAllowScan || currentTeam?.distAllowDirect || currentTeam?.distAllowCheckin || currentTeam?.distAllowView || currentTeam?.distAllowLinkWallet) && (
            <button
              onClick={() => {
                setDistributorScannerOpen(true);
                setScannedTeamId(null);
                setBasket({});
                setDistributorScanError('');
                setDistributorUnlinkedQr(null);
                setDistributorLinkTarget(null);
                setDistributorSuccessMsg('');
                // Auto-select a valid default tab
                if (currentTeam?.distAllowScan) {
                  setDistTabSub('scan');
                } else if (currentTeam?.distAllowLinkWallet) {
                  setDistTabSub('scan');
                } else if (currentTeam?.distAllowView) {
                  setDistTabSub('list');
                } else if (currentTeam?.isDistributor && currentTeam?.distAllowDirect) {
                  setDistTabSub('search');
                } else {
                  setDistTabSub('scan'); // fallback (will show scanner blocked if no perm)
                }
                // Auto-select mode: distribution only if isDistributor, otherwise checkin
                if (!currentTeam?.isDistributor && currentTeam?.distAllowCheckin) {
                  setDistributorMode('checkin');
                }
              }}
              className="p-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white border border-indigo-500 shadow-md shadow-indigo-600/30 transition-all flex items-center justify-center gap-1.5 active:scale-95"
              title="Scanner / Distribuer"
            >
              <QrCode className="w-4 h-4" />
              <span className="text-[10px] font-black uppercase tracking-wider hidden xs:block">Caisse</span>
            </button>
          )}

          {/* Bouton notifications */}
          <button
            onClick={requestPermission}
            title={permissionState === 'granted' ? `Notifications ${fcmReady ? 'actives' : fcmError || fcmStep}` : 'Activer les notifications'}
            className={`p-2 rounded-xl border transition-all ${
              permissionState === 'granted' && fcmReady
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : permissionState === 'denied'
                ? 'bg-red-500/10 border-red-500/30 text-red-400'
                : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-white hover:border-indigo-500/50'
            }`}
          >
            <Bell className="w-4 h-4" />
          </button>
                    {/* Déconnexion */}
          <button onClick={handleLogout} className="p-2 bg-[#1A1D23] rounded-xl text-slate-400 hover:text-white border border-[#2D3139] hover:border-indigo-500/50 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
          
        </div>
      </div>
      
      {/* Team Focus Bar — Retractable Chronological Timeline */}
      {(() => {
        const now = new Date();
        // Show all shifts of the active project in chronological order
        const myActiveShifts = shifts
          .filter(s => s.teamId === currentTeam.id || (currentTeam.parentArtistId && s.teamId === currentTeam.parentArtistId))
          .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

        if (myActiveShifts.length === 0) return null;

        return (
          <div className="relative z-[45]">
            <div className={`bg-[#14161B] border-b border-[#2D3139] transition-all duration-500 ease-in-out overflow-hidden shadow-2xl ${isFocusBarOpen ? 'max-h-32 pt-2 pb-3 opacity-100' : 'max-h-0 py-0 opacity-0'}`}>
              <div className="px-4 mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full bg-indigo-500 animate-pulse" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">
                    {currentTeam.isArtist ? "Your Scene Line-up & Soundchecks ⚡" : "Your Mission Timeline"}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-0 overflow-x-auto px-4 custom-scrollbar pb-2">
                {myActiveShifts.map((shift, idx) => {
                  const p = points.find(point => point.id === shift.pointId);
                  if (!p || !p.location) return null;

                  const startTime = new Date(shift.start);
                  const endTime = new Date(shift.end);
                  const isActive = now >= startTime && now <= endTime;
                  const isNext = !isActive && idx === (myActiveShifts.findIndex(s => new Date(s.start) > now));
                  
                  return (
                    <div key={shift.id} className="flex items-center">
                      {/* Connection Line */}
                      {idx > 0 && (
                        <div className={`w-10 h-[2px] ${isActive ? 'bg-indigo-500' : isNext ? 'bg-indigo-500/30' : 'bg-[#2D3139]'}`} />
                      )}
                      
                      <button
                        onClick={() => {
                          useStore.getState().setMapFocus({ lat: p.location!.lat, lng: p.location!.lng, zoomLevel: 18 });
                          setActiveTab('map');
                          setIsFocusBarOpen(false);
                        }}
                        className={`relative flex flex-row items-center gap-3 px-4 py-2.5 rounded-2xl transition-all duration-500 active:scale-95 group
                          ${isActive 
                            ? 'bg-emerald-500/20 border-2 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                            : isNext
                              ? 'bg-indigo-500/10 border-2 border-indigo-500/50 shadow-lg'
                              : 'bg-[#1A1D23] border border-[#2D3139] opacity-70 hover:opacity-100'}`}
                      >
                        {/* Status Badge */}
                        <div className="absolute -top-1 left-4 flex items-center gap-1.5 z-10">
                          {isActive && (
                            <span className="bg-emerald-500 text-white px-2 py-0.5 rounded-full text-[7px] font-black tracking-widest animate-pulse shadow-lg shadow-emerald-500/50">ACTIVE</span>
                          )}
                        </div>
                        
                        {/* Icon on the Left - Pulsing if Next */}
                        <div className={`p-2 rounded-xl transition-all flex-shrink-0 
                          ${isActive 
                            ? 'bg-emerald-600 text-white shadow-emerald-500/50 shadow-lg' 
                            : isNext 
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.1)] animate-[pulse_3s_infinite]' 
                              : 'bg-[#2D3139] text-slate-400 group-hover:text-indigo-400'}`}>
                          <PointIcon name={p.iconName || 'MapPin'} size={18} />
                        </div>
                        
                        {/* Text Content on the Right */}
                        <div className="flex flex-col items-start min-w-0 pr-1">
                          <span className={`text-[11px] font-black whitespace-nowrap tracking-tight truncate max-w-[120px] ${isActive ? 'text-white' : isNext ? 'text-emerald-50' : 'text-slate-400'}`}>
                            {p.name}
                          </span>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`text-[9px] font-bold font-mono ${isActive ? 'text-emerald-400' : isNext ? 'text-emerald-500/70' : 'text-slate-500'}`}>
                              {startTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                            <span className="text-[9px] text-slate-600">—</span>
                            <span className={`text-[9px] font-bold font-mono ${isActive ? 'text-emerald-400' : isNext ? 'text-emerald-500/70' : 'text-slate-500'}`}>
                              {endTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                            <span className="text-[9px] text-slate-600 ml-1 opacity-40">|</span>
                            <span className={`text-[9px] font-bold font-mono ml-1 ${isActive ? 'text-emerald-400' : isNext ? 'text-emerald-500/70' : 'text-slate-500'}`}>
                              {startTime.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'})}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Languette / Toggle */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full">
              <button 
                onClick={() => setIsFocusBarOpen(!isFocusBarOpen)}
                className="bg-[#14161B] border border-t-0 border-[#2D3139] px-6 py-1.5 rounded-b-2xl text-[10px] font-black text-slate-400 hover:text-white transition-all flex items-center gap-2.5 shadow-2xl group active:pt-3"
              >
                <div className={`w-2 h-2 rounded-full transition-all duration-500 ${isFocusBarOpen ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]' : 'bg-slate-600 group-hover:bg-indigo-400'}`} />
                <span className="tracking-[0.1em] uppercase">
                  {isFocusBarOpen ? 'COLLAPSE' : (currentTeam.isArtist ? 'SCENE TIMELINE ⚡' : 'MISSION TIMELINE')}
                </span>
                <motion.div
                  animate={{ rotate: isFocusBarOpen ? 180 : 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <ChevronDown className="w-3.5 h-3.5 text-indigo-400" />
                </motion.div>
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main Content Area */}
      <div 
        ref={contentAreaRef}
        className="flex-1 overflow-hidden relative bg-gradient-to-br from-[#0A0B0E] to-[#14161B]"
      >
        {/* Pull-to-Refresh Indicator */}
        {(pullDistance > 0 || isRefreshing) && (
          <div 
            className={`absolute top-4 inset-x-0 flex items-center justify-center pointer-events-none z-50 ${!isReadyToPull.current ? 'transition-all duration-300 ease-out' : ''}`}
            style={{ 
              transform: `translateY(${isRefreshing ? 20 : pullDistance - 40}px)`,
              opacity: Math.min((isRefreshing ? 60 : pullDistance) / 45, 1)
            }}
          >
            <div className="bg-[#14161B]/95 backdrop-blur-md border border-[#2D3139] px-4 py-2.5 rounded-2xl shadow-[0_10px_25px_rgba(0,0,0,0.6)] flex items-center gap-2.5">
              <div className={`w-4 h-4 border-[2px] border-indigo-500 border-t-transparent rounded-full ${isRefreshing || pullDistance >= 55 ? 'animate-spin' : ''}`}
                   style={{ transform: !isRefreshing && pullDistance < 55 ? `rotate(${pullDistance * 6}deg)` : undefined }} />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-200">
                {isRefreshing ? 'Mise à jour...' : pullDistance >= 55 ? 'Relâchez pour rafraîchir' : 'Tirez pour rafraîchir'}
              </span>
            </div>
          </div>
        )}

        {/* Ambient background glows */}
        <div className="absolute top-[-10%] left-[-10%] w-[300px] h-[300px] bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[300px] h-[300px] bg-emerald-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        {activeTab === 'map' && (globalSettings.mapProvider === 'google' ? <MapView isTeamView={true} /> : <MapViewMapbox isTeamView={true} />)}
        {activeTab === 'timeline' && <GanttTimeline />}
        {activeTab === 'badge' && (
          <div className="h-full overflow-y-auto pt-12 pb-32 px-5 sm:p-5 sm:pb-32 space-y-6 flex flex-col items-center custom-scrollbar relative z-10" style={{ overscrollBehaviorY: 'contain' }}>
            
            {/* 1. Header Hero - Prochain Shift */}
            {currentShift ? (
              <div className="w-full max-w-sm relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
                <div className="relative bg-[#14161B] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 p-4">
                    {currentTeam.isOnDuty ? (
                      <span className="bg-emerald-500 text-white text-[9px] font-black px-2 py-1 rounded-full border border-emerald-500/20 tracking-widest shadow-lg shadow-emerald-500/30 animate-pulse">EN SERVICE</span>
                    ) : (
                      <span className="bg-indigo-500/10 text-indigo-400 text-[9px] font-black px-2 py-1 rounded-full border border-indigo-500/20 tracking-widest">CONFIRMÉ</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" /> Prochaine Mission
                    </h3>
                    <div className="flex items-start gap-4">
                      <div className="p-3.5 bg-gradient-to-br from-indigo-500/20 to-purple-500/10 rounded-2xl border border-white/5 text-indigo-400">
                        <PointIcon name={points.find(p => p.id === currentShift.pointId)?.iconName || 'MapPin'} size={32} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-black text-white text-xl leading-tight truncate mb-1">
                          {points.find(p => p.id === currentShift.pointId)?.name}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-indigo-400 font-black font-mono text-sm">
                            {new Date(currentShift.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                          <span className="text-slate-600 text-xs">—</span>
                          <span className="text-indigo-400/70 font-bold font-mono text-sm">
                            {new Date(currentShift.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-center justify-center">
                        {!currentTeam.isOnDuty ? (
                          <button
                            onClick={() => setScannerOpen(true)}
                            className="flex flex-col items-center justify-center p-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/25 transition-all active:scale-95 border border-indigo-400/20 group/scan"
                          >
                            <QrCode className="w-6 h-6 text-white group-hover/scan:scale-110 transition-transform" />
                            <span className="text-[9px] font-black uppercase tracking-wider text-indigo-200 mt-1">scan service</span>
                          </button>
                        ) : (
                          <button
                            onClick={handleCheckOut}
                            className="flex flex-col items-center justify-center p-3 rounded-2xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 shadow-lg shadow-red-500/5 transition-all active:scale-95 group/scan"
                          >
                            <LogOut className="w-6 h-6 text-red-400 group-hover/scan:scale-110 transition-transform" />
                            <span className="text-[9px] font-black uppercase tracking-wider text-red-400 mt-1">fin service</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-3xl p-8 text-center backdrop-blur-md">
                 <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/5">
                   <Clock className="w-8 h-8 text-slate-600" />
                 </div>
                 <p className="text-white font-bold text-lg">Aucune mission</p>
                 <p className="text-slate-400 text-xs mt-1">Vous n'avez pas de shift prévu prochainement.</p>
              </div>
            )}

            {/* 2. Main Profile Identity Card */}
            <div className="w-full max-w-sm relative group">
              {currentTeam.isArtist ? (
                <>
                  <div className="absolute -inset-0.5 bg-gradient-to-b from-amber-500/40 to-yellow-500/10 rounded-[2.5rem] blur opacity-30 animate-[pulse_4s_infinite]"></div>
                  <div className="relative bg-gradient-to-br from-[#1E1B18] to-[#0A0B0E] border border-amber-500/30 rounded-[2.5rem] p-8 shadow-2xl flex flex-col items-center overflow-hidden">
                    
                    {/* Decorative gold background elements */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-500/5 rounded-full blur-[80px]"></div>
                    <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-yellow-500/5 rounded-full blur-[80px]"></div>
                    
                    {/* Profile Photo with Gold Ring */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-amber-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
                      <div className="w-32 h-32 rounded-[2.5rem] bg-[#0A0B0E] flex items-center justify-center border-4 border-amber-500/40 shadow-[0_0_25px_rgba(245,158,11,0.2)] overflow-hidden relative z-10 group-hover:scale-105 transition-transform duration-500">
                        {currentTeam.photo ? (
                          <img src={currentTeam.photo} alt={currentTeam.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-16 h-16 text-amber-500/50" />
                        )}
                      </div>
                      <div className="absolute -bottom-2 -right-2 bg-amber-500 p-1.5 rounded-xl border-4 border-[#0A0B0E] shadow-lg z-20">
                        <Music className="w-3.5 h-3.5 text-[#0A0B0E]" />
                      </div>
                    </div>

                    {/* Identity Info */}
                    <div className="text-center z-10 w-full px-2">
                      <h2 className="text-3xl font-black text-amber-400 tracking-tighter mb-1 leading-none uppercase truncate">
                        {currentTeam.name}
                      </h2>
                      <div className="flex items-center justify-center gap-2 mb-8">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        <span className="text-amber-500/70 font-black tracking-[0.25em] uppercase text-[10px]">ARTISTE 👑</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      </div>
 
                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-3 w-full">
                        <div className="bg-amber-500/5 border border-amber-500/10 rounded-2xl p-4 text-left group-hover:bg-amber-500/10 transition-colors">
                          <span className="text-[9px] font-black text-amber-500/60 uppercase tracking-widest block mb-1">Style</span>
                          <p className="text-xs font-bold text-white leading-snug line-clamp-2">
                            {(currentTeam.roles || [currentTeam.role]).join(', ')}
                          </p>
                        </div>
                        <div className="bg-amber-500/5 border border-amber-500/10 rounded-2xl p-4 text-left group-hover:bg-amber-500/10 transition-colors">
                          <span className="text-[9px] font-black text-amber-500/60 uppercase tracking-widest block mb-1">Identifiant Artiste</span>
                          <p className="text-sm font-black font-mono text-amber-400 truncate">
                            #{currentTeam.id.slice(0,8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute -inset-0.5 bg-gradient-to-b from-indigo-500/30 to-purple-500/10 rounded-[2.5rem] blur opacity-10"></div>
                  <div className="relative bg-gradient-to-br from-[#1A1D23] to-[#14161B] border border-white/10 rounded-[2.5rem] p-8 shadow-2xl flex flex-col items-center overflow-hidden">
                    
                    {/* Decorative background elements */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-600/5 rounded-full blur-[80px]"></div>
                    <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-emerald-600/5 rounded-full blur-[80px]"></div>
                    
                    {/* Profile Photo */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-20 rounded-full animate-pulse"></div>
                      <div className="w-32 h-32 rounded-[2.5rem] bg-[#0A0B0E] flex items-center justify-center border-4 border-white/10 shadow-inner overflow-hidden relative z-10 group-hover:scale-105 transition-transform duration-500">
                        {currentTeam.photo ? (
                          <img src={currentTeam.photo} alt={currentTeam.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-16 h-16 text-indigo-500/50" />
                        )}
                      </div>
                      <div className="absolute -bottom-2 -right-2 bg-emerald-500 p-1.5 rounded-xl border-4 border-[#14161B] shadow-lg z-20">
                        <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </div>

                    {/* Identity Info */}
                    <div className="text-center z-10 w-full px-2">
                      <h2 className="text-3xl font-black text-white tracking-tighter mb-1 leading-none uppercase truncate">
                        {currentTeam.name}
                      </h2>
                      <div className="flex items-center justify-center gap-2 mb-8">
                                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                        <span className="text-indigo-400 font-black tracking-[0.25em] uppercase text-[10px]">
                          {teamOrgs.length > 1
                            ? `Team ${teamOrgs.map(o => o.name?.split(' ')[0]).join(' & ')}`
                            : `Team ${currentOrg?.name?.split(' ')[0] || ''}`}
                        </span>
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-3 w-full">
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-4 text-left group-hover:bg-white/10 transition-colors">
                          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Rôles</span>
                          <p className="text-xs font-bold text-white leading-snug line-clamp-2">
                            {(currentTeam.roles || [currentTeam.role]).join(', ')}
                          </p>
                        </div>
                        <div className="bg-white/5 border border-white/5 rounded-2xl p-4 text-left group-hover:bg-white/10 transition-colors">
                          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Identifiant</span>
                          <p className="text-sm font-black font-mono text-indigo-400">
                            #{currentTeam.id.slice(0,8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Vector QR Code & Real-Time Remaining Quota Chips */}
            {(() => {
              const orgProducts = offerProducts.filter(p => myOrgIds.includes(p.orgId || ''));

              return (
                <>
                  {/* Badge Digital QR Code */}
                  <div className="w-full max-w-sm relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur opacity-25"></div>
                    <div className="relative bg-[#14161B] border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col items-center">
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">
                        Votre QR Code Badge
                      </h3>
                      
                      <div className="bg-white p-3 rounded-2xl shadow-inner mb-4">
                        <QRCode value={currentTeam.id} size={150} fgColor="#0A0B0E" bgColor="#FFFFFF" />
                      </div>

                      <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest text-center px-4 leading-normal">
                        Présentez ce code aux points de ravitaillement ou de check-in
                      </p>
                    </div>
                  </div>

                  {/* Link My Wallet */}
                  <div className="w-full max-w-sm bg-[#14161B] border border-[#2D3139]/80 rounded-3xl p-5 shadow-2xl">
                    {/* Success banner */}
                    {walletLinkSuccess && (
                      <div className="mb-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-center flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-[10px] font-bold text-emerald-300">{walletLinkSuccess}</span>
                        </div>
                        <button onClick={() => setWalletLinkSuccess('')} className="text-emerald-400 hover:text-emerald-300 shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-between pb-2.5 border-b border-[#2D3139]/50 mb-3">
                      <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                        <Link className="w-4 h-4 text-purple-400" />
                        Link My Wallet
                      </h4>
                      {currentTeam.linkedWalletQr ? (
                        <span className="text-[9px] font-bold text-emerald-400 uppercase flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          Lié
                        </span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-500 uppercase">Non lié</span>
                      )}
                    </div>

                    {currentTeam.linkedWalletQr ? (
                      /* Wallet is linked */
                      <div className="space-y-2">
                        <div className="p-3 bg-[#1C1F26] rounded-2xl border border-emerald-500/20">
                          <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">QR Code lié</p>
                          <p className="text-[10px] font-mono text-emerald-400 break-all">
                            {currentTeam.linkedWalletQr}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleReplaceWallet}
                            className="flex-1 py-2.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 font-black text-[10px] uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Remplacer
                          </button>
                          <button
                            onClick={handleUnlinkWallet}
                            className="flex-1 py-2.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 font-black text-[10px] uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Dissocier
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Wallet not linked */
                      <div className="space-y-2">
                        <p className="text-[9px] text-slate-500 leading-relaxed">
                          Scannez un QR code externe (bracelet, carte) pour le lier à votre compte.
                          Il servira pour vos consommations et check-ins.
                        </p>
                        <button
                          onClick={handleLinkWallet}
                          className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-[11px] uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20"
                        >
                          <QrCode className="w-4 h-4" />
                          Scanner un QR Code
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Remaining Quotas — grouped by active project */}
                  {(() => {
                    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
                    // Group products by projectId (fallback to orgId if no projectId)
                    const groups: Record<string, {
                      projectName: string; projectId?: string; orgId: string; orgName: string;
                      endDate?: string; products: typeof orgProducts;
                    }> = {};
                    orgProducts.forEach(prod => {
                      const key = prod.projectId || prod.orgId;
                      if (!groups[key]) {
                        const proj = prod.projectId ? projects.find(p => p.id === prod.projectId) : null;
                        const org = organizations.find(o => o.id === prod.orgId);
                        groups[key] = {
                          projectName: proj ? proj.name : (org?.name || 'Organisation'),
                          projectId: prod.projectId,
                          orgId: prod.orgId,
                          orgName: org?.name || 'Organisation',
                          endDate: proj?.endDate,
                          products: [],
                        };
                      }
                      groups[key].products.push(prod);
                    });

                    // Filter: only show active projects (endDate >= today or no endDate)
                    const activeGroups = Object.values(groups)
                      .filter(g => !g.endDate || g.endDate >= today);

                    if (activeGroups.length === 0) return null;

                    return activeGroups.map((projGroup: typeof activeGroups[0], idx: number) => {
                      const org = organizations.find(o => o.id === projGroup.orgId);
                      const projLastReset = org?.settings && (org.settings as any).last_quota_reset
                        ? new Date((org.settings as any).last_quota_reset)
                        : null;

                      return (
                        <div key={projGroup.projectId || projGroup.orgId} className="w-full max-w-sm bg-[#14161B] border border-[#2D3139]/80 rounded-3xl p-5 shadow-2xl space-y-4">
                          <div className="flex items-center justify-between pb-2.5 border-b border-[#2D3139]/50">
                            <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                              <Gift className="w-4 h-4 text-indigo-400" />
                              {idx === 0 ? 'Vos Consommations Restantes' : ' '}
                            </h4>
                            <span className="text-[9px] font-bold text-indigo-400 uppercase bg-indigo-500/10 px-2 py-0.5 rounded-full border border-indigo-500/20">
                              {projGroup.projectName} Credits
                            </span>
                          </div>

                          <div className="grid grid-cols-1 gap-2.5">
                            {projGroup.products.map((prod: typeof orgProducts[0]) => {
                              const totalQuota = getEffectiveQuota(currentTeam.id, prod.name, prod);

                              // Sum up consumptions for this product after last reset
                              const sumConsumed = consumptions
                                .filter((c: any) => c.teamId === currentTeam.id && c.productName === prod.name && (!projLastReset || new Date(c.createdAt) >= projLastReset))
                                .reduce((sum: number, c: any) => sum + c.quantity, 0);

                              const remaining = Math.max(0, totalQuota - sumConsumed);
                              const percent = totalQuota > 0 ? (remaining / totalQuota) * 100 : 0;

                              return (
                                <div key={prod.id} className="p-3 bg-[#1C1F26] rounded-2xl border border-white/5 flex flex-col gap-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-200">{prod.name}</span>
                                    <span className={`text-[10px] font-black font-mono ${remaining > 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
                                      {remaining} / {totalQuota} restants
                                    </span>
                                  </div>
                                  <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${remaining > 0 ? 'bg-gradient-to-r from-indigo-500 to-emerald-500' : 'bg-rose-500'}`}
                                      style={{ width: `${percent}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              );
            })()}

            {/* 3. FCM Status - Subtler Design */}
            <div className="w-full max-w-sm bg-black/30 border border-white/5 rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/5 rounded-xl"><Bell className="w-4 h-4 text-slate-400" /></div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Connectivité</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${permissionState === 'granted' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-red-500'}`}></div>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{permissionState === 'granted' ? 'Active' : 'Inactif'}</span>
                </div>
              </div>
              
              {permissionState !== 'granted' && (
                <div className="space-y-3">
                  <p className="text-[10px] text-slate-500 leading-relaxed text-center px-4">
                    Activez les notifications pour recevoir vos alertes de mission en temps réel.
                  </p>
                  <button
                    onClick={requestPermission}
                    className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-black text-[11px] uppercase tracking-widest transition-all"
                  >
                    Activer les alertes
                  </button>
                </div>
              )}
            </div>

            {/* 4. Custom buttons */}
            <div className="w-full max-w-sm space-y-3">
              {(currentTeam.customButtons || []).filter(b => b.label && b.url).map((btn, idx) => {
                const colorMap: Record<string, string> = {
                  indigo: 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-600/20',
                  emerald: 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/20',
                  amber: 'bg-amber-600 hover:bg-amber-500 shadow-amber-600/20',
                  rose: 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/20',
                  sky: 'bg-sky-600 hover:bg-sky-500 shadow-sky-600/20',
                  purple: 'bg-purple-600 hover:bg-purple-500 shadow-purple-600/20',
                };
                return (
                  <a key={idx} href={btn.url} target="_blank" rel="noreferrer"
                    className={`w-full py-4 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] flex items-center justify-center gap-3 shadow-xl transition-all active:scale-95 ${colorMap[btn.color] || colorMap.indigo} text-white`}>
                    <Link className="w-4 h-4" />{btn.label}
                  </a>
                );
              })}
            </div>

            {/* 5. Unavailability Picker */}
            {unavailabilityAllowedOnAnyShift && (
              <div className="w-full max-w-sm bg-[#14161B]/80 backdrop-blur border border-[#2D3139] rounded-3xl p-6 shadow-xl">
                <h3 className="font-bold text-white mb-2 text-lg">Mes Indisponibilités</h3>
                <p className="text-xs text-slate-400 mb-5 leading-relaxed">Sélectionnez les jours où vous ne serez pas disponible.</p>
                <input type="date"
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-colors"
                  onChange={(e) => { if (e.target.value) { const d = currentTeam.unavailableDates || []; if (!d.includes(e.target.value)) updateTeam(currentTeam.id, { unavailableDates: [...d, e.target.value] }); e.target.value = ''; } }}
                />
                <div className="flex flex-wrap gap-2 mt-4">
                  {(currentTeam.unavailableDates || []).map(date => (
                    <div key={date} className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 font-medium">
                      <span>{new Date(date).toLocaleDateString('fr-FR', {weekday: 'short', day: 'numeric', month: 'short'})}</span>
                      <button onClick={() => updateTeam(currentTeam.id, { unavailableDates: (currentTeam.unavailableDates || []).filter(d => d !== date) })} className="hover:text-white bg-red-500/20 rounded-full w-4 h-4 flex items-center justify-center">&times;</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Issue Modal */}
            {issueModalOpen && (
              <div className="absolute inset-0 bg-[#0A0B0E]/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-[#14161B] border border-[#2D3139] p-6 rounded-2xl w-full max-w-sm shadow-2xl">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center"><AlertTriangle className="w-6 h-6 mr-3 text-amber-500"/> Signaler un incident</h3>
                  <textarea className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50 mb-5 resize-none" rows={4} placeholder="Besoin d'eau, problème matériel, retard..." value={issueMessage} onChange={(e) => setIssueMessage(e.target.value)} />
                  <div className="flex space-x-3">
                    <button onClick={() => setIssueModalOpen(false)} className="flex-1 py-3 text-sm text-slate-300 bg-[#1A1D23] hover:bg-[#2D3139] rounded-xl font-bold">Annuler</button>
                    <button onClick={submitIssue} disabled={!issueMessage.trim()} className="flex-1 py-3 text-sm bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-xl font-bold">Envoyer</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'swaps' && (
          <div className="h-full overflow-y-auto pt-12 pb-32 px-6 sm:p-6 sm:pb-32 space-y-6 custom-scrollbar" style={{ overscrollBehaviorY: 'contain' }}>
            <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/50 rounded-3xl p-6 shadow-xl relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
               <h2 className="text-3xl font-bold text-white flex items-center mb-2"><RefreshCw className="mr-3 w-8 h-8 text-indigo-400" /> Bourse d'Échanges</h2>
               <p className="text-slate-400 text-sm leading-relaxed">Prenez des shifts abandonnés par d'autres teams ou proposez le vôtre.</p>
            </div>
            
            <div className="space-y-4 pt-4">
              <h3 className="font-semibold text-white">Shifts Disponibles</h3>
              {swapableShifts.length === 0 ? (
                <p className="text-slate-500 italic">Aucun shift disponible pour échange.</p>
              ) : (
                swapableShifts.map(shift => {
                  const point = points.find(p => p.id === shift.pointId);
                  const teamObj = team.find(s => s.id === shift.teamId);
                  return (
                    <div key={shift.id} className="bg-[#14161B] border border-[#2D3139] p-4 rounded-xl flex flex-col hover:border-indigo-500 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                           <h4 className="font-medium text-white">{point?.name}</h4>
                           <p className="text-xs text-slate-400">Proposé par {teamObj?.name}</p>
                        </div>
                        <span className="bg-indigo-500/20 text-indigo-400 text-xs px-2 py-1 rounded">Récupérable</span>
                      </div>
                      <div className="text-sm text-slate-300 mb-4">
                         {new Date(shift.start).toLocaleString('fr-FR', {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})} - {new Date(shift.end).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                      </div>
                      <button 
                        onClick={() => {
                          setConfirmAction({
                            message: 'Voulez-vous vraiment prendre ce shift ?',
                            onConfirm: () => updateShift(shift.id, { teamId: currentTeam.id, swapRequested: false })
                          });
                        }}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 rounded-lg text-sm"
                      >
                         Accepter le shift
                      </button>
                    </div>
                  )
                })
              )}
            </div>

            <div className="space-y-4 pt-8 border-t border-[#2D3139] mt-8">
              <h3 className="font-semibold text-white">Vos Shifts (Proposer à l'échange)</h3>
              {currentOrNextShifts.length === 0 ? (
                 <p className="text-slate-500 italic">Vous n'avez aucun shift à venir.</p>
              ) : (
                 currentOrNextShifts.map(shift => {
                    const point = points.find(p => p.id === shift.pointId);
                    const cat = categories.find(c => c.id === point?.categoryId);
                    const isGeneral = cat?.isGeneral;
                    const canSwap = point?.swapEnabled === true && !isGeneral;
                    
                    return (
                      <div key={shift.id} className="bg-[#14161B] border border-[#2D3139] p-4 rounded-xl flex justify-between items-center">
                         <div>
                           <p className="font-medium text-white">{point?.name}</p>
                           <p className="text-xs text-slate-400">{new Date(shift.start).toLocaleString('fr-FR', {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'})}</p>
                         </div>
                         {canSwap && (
                           shift.swapRequested ? (
                              <button 
                                onClick={() => updateShift(shift.id, { swapRequested: false })}
                                className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded"
                              >
                                 Annuler la demande
                              </button>
                           ) : (
                              <button 
                                onClick={() => updateShift(shift.id, { swapRequested: true })}
                                className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-2 rounded border border-red-500/20"
                              >
                                 Proposer
                              </button>
                           )
                         )}
                      </div>
                    )
                 })
              )}
            </div>
          </div>
        )}
        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="h-full overflow-y-auto overflow-x-hidden pt-6 pb-32 px-3 sm:p-5 sm:pb-32 space-y-4 flex flex-col items-center custom-scrollbar relative z-10" style={{ overscrollBehaviorY: 'contain' }}>
            <div className="w-full max-w-lg">
               <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-indigo-500/20 border border-indigo-500/30"><Send className="w-5 h-5 text-indigo-400" /></div>
                  <div>
                    <h2 className="font-bold text-white text-lg">Messages</h2>
                    <p className="text-xs text-slate-400">{unreadMessages.length > 0 ? `${unreadMessages.length} non lu(s)` : 'Tous lus'}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIssuePointId(currentShift?.pointId || (points[0]?.id || ''));
                    setIssueType('incident_public');
                    setIssueModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 text-red-400 rounded-xl text-xs font-bold transition-all shadow-lg shadow-red-500/5 active:scale-95 flex-shrink-0"
                >
                  <AlertTriangle className="w-3.5 h-3.5" /> Signaler un incident
                </button>
              </div>

              {/* ── Contacts List ── */}
              <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/50 rounded-2xl p-4 space-y-3 mb-4">
                <button
                  onClick={() => setShowNewMessage(!showNewMessage)}
                  className="w-full flex items-center gap-2 text-left focus:outline-none"
                >
                  <Users className="w-4 h-4 text-indigo-400" />
                  <h3 className="font-bold text-white text-xs">Contacts</h3>
                  {selectedContactIds.length > 0 && (
                    <span className="text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 px-1.5 py-0.5 rounded-full font-extrabold">
                      {selectedContactIds.length} sélectionné{selectedContactIds.length > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="ml-auto text-slate-400">
                    {showNewMessage ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </span>
                </button>

                {showNewMessage && (
                  <div className="space-y-3 pt-2 border-t border-[#2D3139]/30 max-h-64 overflow-y-auto custom-scrollbar">
                    {/* Unified contact list from deduped allContacts, grouped by role */}
                    {(() => {
                      const staffList = allContacts.filter(c => c.role === 'staff');
                      // Artists only see their assigned team member, not managers/admins
                      const mgrList = isArtistUser ? [] : allContacts.filter(c => c.role === 'manager');
                      const admList = isArtistUser ? [] : allContacts.filter(c => c.role === 'admin');
                      if (isArtistUser && !assignedTeamMember) return <p className="text-xs text-amber-500 italic text-center py-4">Personne n'est affecté à votre suivi. Contactez votre manager.</p>;
                      if (allContacts.length === 0) return <p className="text-xs text-slate-500 italic text-center py-4">Aucun contact</p>;

                      const renderContact = (c: ContactEntry) => {
                        const unread = getUnreadFrom({ name: c.name, email: c.email });
                        const bestMatch = findBestTeamByEmail(team, c.email);
                        const realName = bestMatch?.name;
                        const displayName = realName || c.name;
                        const isArtistContact = !!bestMatch?.isArtist;
                        // Email validation: staff needs confirmed email, managers/admins always valid
                        const emailOk = c.role !== 'staff' || !!(c.email && c.email.includes('@') && bestMatch?.emailConfirmed);
                        const roleIcons: Record<string, JSX.Element> = {
                          staff: <Users className="w-3 h-3" />,
                          manager: <Crown className="w-3 h-3" />,
                          admin: <ShieldCheck className="w-3 h-3" />,
                        };
                        const roleColors: Record<string, string> = {
                          staff: 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400',
                          manager: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
                          admin: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
                        };
                        const selectedColors: Record<string, string> = {
                          staff: 'bg-emerald-500/10 border-emerald-500/30',
                          manager: 'bg-indigo-500/10 border-indigo-500/30',
                          admin: 'bg-amber-500/10 border-amber-500/30',
                        };
                        return (
                          <div key={c.id} className={`w-full flex items-center gap-2.5 p-2 rounded-xl transition-all text-xs ${
                            !emailOk ? 'opacity-50' :
                            selectedContactIds.includes(c.id) ? selectedColors[c.role] :
                            selectedConversationId === c.id ? selectedColors[c.role] :
                            'bg-[#1A1D23]/60 border border-transparent hover:border-[#2D3139]'
                          }`}>
                            <button
                              onClick={() => emailOk ? openConversation({ name: c.name, id: c.id, email: c.email }) : null}
                              disabled={!emailOk}
                              title={!emailOk ? 'Email non validé — Contact en lecture seule' : undefined}
                              className="flex items-center gap-2.5 flex-1 min-w-0 text-left relative"
                            >
                              <div className="relative flex-shrink-0">
                                {c.photo ? <img src={c.photo} className="w-6 h-6 rounded-lg object-cover" alt={displayName} /> :
                                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-[9px] ${roleColors[c.role]}`}>{displayName.substring(0,2).toUpperCase()}</div>}
                                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full animate-orange-blink border border-[#0A0B0E]" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-left truncate text-slate-300 block text-[11px] font-medium">{displayName}</span>
                                <span className="text-[8px] text-slate-500 truncate block leading-tight">
                                  {roleIcons[c.role]} {roleLabel(c)}
                                  {isArtistContact && <span className="text-amber-400 font-bold ml-1">👑 Artiste</span>}
                                  {c.secondaryRoles.length > 0 && <span> · {c.secondaryRoles.join(', ')}</span>}
                                  {c.orgName ? ` · ${c.orgName}` : ''}
                                  {!emailOk && c.role === 'staff' && <span className="text-amber-500 ml-1">⚠️ Mail non validé</span>}
                                </span>
                              </div>
                            </button>
                            <button onClick={() => { setSelectedContactIds(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]); setIsSelectAllCategory(false); setSelectedConversationId(null); }} className="flex-shrink-0">
                              {selectedContactIds.includes(c.id) ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <div className="w-4 h-4 rounded-full border border-[#2D3139] hover:border-emerald-400/50" />}
                            </button>
                          </div>
                        );
                      };

                      return (
                        <>
                          {staffList.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider">Teams</p>
                                <button
                                  onClick={() => {
                                    if (isSelectAllCategory && selectedContactIds.length >= allCategoryCount) {
                                      setSelectedContactIds([]); setIsSelectAllCategory(false);
                                    } else {
                                      setSelectedContactIds(categoryTeam.map(s => s.id)); setIsSelectAllCategory(true);
                                    }
                                  }}
                                  className={`text-[8px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                                    isSelectAllCategory && selectedContactIds.length >= allCategoryCount
                                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                      : 'bg-[#2D3139] text-slate-400 border border-[#2D3139] hover:text-white'
                                  }`}
                                >
                                  {isSelectAllCategory && selectedContactIds.length >= allCategoryCount ? '✓ Toute la catégorie' : 'Toute la catégorie'}
                                </button>
                              </div>
                              <div className="space-y-1">{staffList.map(renderContact)}</div>
                            </div>
                          )}
                          {mgrList.length > 0 && (
                            <div>
                              <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Managers</p>
                              <div className="space-y-1">{mgrList.map(renderContact)}</div>
                            </div>
                          )}
                          {admList.length > 0 && (
                            <div>
                              <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Admins</p>
                              <div className="space-y-1">{admList.map(renderContact)}</div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* ── Group Message Composer (when checkboxes selected) ── */}
              {selectedContactIds.length > 0 && !selectedConversationId && (
                <div className="bg-[#0A0B0E]/60 border border-emerald-500/20 rounded-2xl p-4 space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-emerald-400" />
                    <h3 className="font-bold text-white text-xs">
                      {selectedContactIds.length === 1 ? 'Message à 1 contact' : `Groupe · ${selectedContactIds.length} contacts`}
                    </h3>
                  </div>
                  {selectedContactIds.length > 1 && (
                    <input
                      type="text"
                      value={groupName}
                      onChange={e => setGroupName(e.target.value)}
                      placeholder="Nom du groupe (optionnel)..."
                      className="w-full bg-[#1A1D23] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs placeholder-slate-600 focus:border-emerald-500 focus:outline-none"
                    />
                  )}
                  {/* Selected contacts chips */}
                  <div className="flex flex-wrap gap-1">
                    {selectedContactIds.map(id => {
                      const c = allContacts.find(x => x.id === id);
                      return c ? (
                        <span key={id} className="text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                          {c.name}
                          <button onClick={() => setSelectedContactIds(prev => prev.filter(i => i !== id))}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ) : null;
                    })}
                  </div>
                  {/* Media preview */}
                  {messageImage && (<div className="relative inline-block"><img src={messageImage} className="h-12 rounded-lg border border-[#2D3139]" /><button onClick={() => setMessageImage(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"><X className="w-2 h-2 text-white" /></button></div>)}
                  {messageVoice && (<div className="flex items-center gap-1 text-[10px] text-emerald-400"><span>🎤 Prêt</span><audio src={URL.createObjectURL(messageVoice)} controls className="h-5 w-24" /><button onClick={() => setMessageVoice(null)}><X className="w-3 h-3 text-red-400" /></button></div>)}
                  {/* Input */}
                  <div className="flex gap-2">
                    <input ref={fileInputRefTeam} type="file" accept="image/*" className="hidden" onChange={handleTeamImagePick} />
                    <button onClick={() => fileInputRefTeam.current?.click()} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Image className="w-3 h-3" /></button>
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleTeamImagePick} />
                    <button onClick={() => cameraInputRef.current?.click()} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Camera className="w-3 h-3" /></button>
                    {recordingTeam ? (
                      <button onClick={stopTeamRecording} className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse"><Square className="w-3 h-3" /></button>
                    ) : (
                      <button onClick={startTeamRecording} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Mic className="w-3 h-3" /></button>
                    )}
                    <textarea value={messageContent} onChange={e => setMessageContent(e.target.value)} placeholder="Message..." rows={2} className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs placeholder-slate-600 focus:border-emerald-500 focus:outline-none resize-none" />
                    <button onClick={handleSendNewMessage} disabled={(!messageContent.trim() && !messageImage && !messageVoice) || sendingMessage} className="w-9 h-9 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl flex items-center justify-center flex-shrink-0">
                      {sendingMessage ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              )}

              {/* Validation modal */}
              {showValidationModal && (
                <div className="fixed inset-0 bg-[#0A0B0E]/90 backdrop-blur-sm flex items-center justify-center p-4 z-[200]">
                  <div className="bg-[#14161B] border border-amber-500/30 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-500/30">
                        <Megaphone className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-sm">Demande envoyée</h3>
                        <p className="text-[10px] text-slate-400">En attente d'approbation</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-300 mb-4 leading-relaxed">
                      Votre message à <strong>{selectedContactIds.length} destinataires</strong> a été soumis pour validation. Un manager ou admin doit l'approuver avant qu'il soit distribué.
                    </p>
                    <p className="text-[10px] text-amber-400/70 mb-4 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> La demande expire dans 4 heures si elle n'est pas traitée.
                    </p>
                    <button
                      onClick={() => setShowValidationModal(false)}
                      className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl text-sm transition-colors"
                    >
                      Compris
                    </button>
                  </div>
                </div>
              )}

              {/* Pending approval requests from this team */}
              {(approvalRequests || []).filter(r => r.teamId === currentTeam.id && r.status === 'pending').length > 0 && (
                <div className="bg-[#0A0B0E]/60 border border-amber-500/20 rounded-2xl p-4 space-y-3 mb-4">
                  <h3 className="font-bold text-white text-xs flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-amber-400" /> Mes demandes en attente
                  </h3>
                  {(approvalRequests || []).filter(r => r.teamId === currentTeam.id && r.status === 'pending').map(req => (
                    <div key={req.id} className="bg-[#14161B] rounded-xl p-3 border border-[#2D3139] space-y-2">
                      <div className="flex justify-between items-start">
                        <span className="text-[10px] text-slate-400">
                          À: <span className="text-indigo-400">{req.recipientNames.length} destinataires</span>
                        </span>
                        <span className="text-[9px] font-mono text-amber-400">
                          {(() => {
                            const diff = new Date(req.expiresAt).getTime() - Date.now();
                            if (diff <= 0) return 'Expiré';
                            const m = Math.floor(diff / 60000);
                            return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
                          })()}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-300 leading-relaxed line-clamp-2">{req.content}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => relanceRequest(req.id)}
                          className="flex-1 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 font-bold text-[10px] rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          <RefreshCw className="w-2.5 h-2.5" /> Relancer
                        </button>
                        <button
                          onClick={() => cancelRequest(req.id)}
                          className="flex-1 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-bold text-[10px] rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          <XCircle className="w-2.5 h-2.5" /> Annuler
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Conversation view (when a contact is selected) */}
              {selectedConversation && (
                <div className="bg-[#0A0B0E]/60 border border-indigo-500/20 rounded-2xl overflow-visible">
                  {/* Conversation header */}
                  <div className="p-4 border-b border-[#2D3139]/30 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedConversationId(null)} className="text-slate-400 hover:text-white">
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-2.5">
                        {selectedConversation.photo ? (
                          <img src={selectedConversation.photo} className="w-8 h-8 rounded-lg object-cover border border-[#2D3139]" alt={selectedConversation.name} />
                        ) : (
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-[10px] border ${
                            selectedConversation.role === 'staff' ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400' :
                            selectedConversation.role === 'manager' ? 'bg-purple-500/20 border-purple-500/30 text-purple-400' :
                            'bg-amber-500/20 border-amber-500/30 text-amber-400'
                          }`}>
                            {selectedConversation.name.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-white text-xs">{selectedConversation.name}</h3>
                          <p className="text-[9px] text-slate-500">
                            {selectedConversation.role === 'staff' ? 'Team' : selectedConversation.role === 'manager' ? 'Manager' : 'Admin'}
                            {selectedConversation.orgName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Messages */}
                  <div className="p-4 space-y-3 max-h-64 overflow-y-auto custom-scrollbar bg-[#0A0B0E]/40" ref={chatScrollRef}>
                    {conversationMessages.length === 0 ? (
                      <p className="text-xs text-slate-500 italic text-center py-6">Aucun message échangé avec {selectedConversation.name}</p>
                    ) : (
                      conversationMessages.map(m => (
                        <div key={m.id} className={`flex flex-col ${m.senderName === currentTeam.name ? 'items-end' : 'items-start'}`}>
                          <div className={`rounded-2xl px-3.5 py-2 text-xs leading-relaxed max-w-[80%] ${
                            m.senderName === currentTeam.name
                              ? 'bg-emerald-600 text-white rounded-tr-sm'
                              : (m.senderName || '').includes('Alerte') ? 'bg-red-500/10 border border-red-500/30 text-red-300 rounded-tl-sm'
                              : (m.senderName || '').includes('Note') ? 'bg-sky-500/10 border border-sky-500/30 text-sky-300 rounded-tl-sm'
                              : 'bg-[#1C1F26] border border-[#2D3139]/60 text-slate-200 rounded-tl-sm'
                          }`}>
                            {m.content}
                            {m.imageUrl && (
                              <img src={m.imageUrl} className="mt-2 max-w-[160px] max-h-[160px] rounded-lg border border-white/10 cursor-pointer hover:opacity-90" alt="img" onClick={() => window.open(m.imageUrl!, '_blank')} />
                            )}
                            {m.voiceUrl && (
                              <audio src={m.voiceUrl} controls className="mt-2 h-7 max-w-[160px]" preload="metadata" />
                            )}
                          </div>
                          <span className="text-[8px] text-slate-500 mt-1 px-1">
                            {new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Reply composer in conversation */}
                  <div className="p-3 border-t border-[#2D3139]/30 flex flex-col gap-2">
                    {/* Image/Voice previews */}
                    {(messageImage || messageVoice) && (
                      <div className="flex items-center gap-2 px-1">
                        {messageImage && (
                          <div className="relative inline-block">
                            <img src={messageImage} className="h-12 rounded-lg border border-[#2D3139]" alt="preview" />
                            <button onClick={() => setMessageImage(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"><X className="w-2 h-2 text-white" /></button>
                          </div>
                        )}
                        {messageVoice && (
                          <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                            <span>🎤 Prêt</span>
                            <audio src={URL.createObjectURL(messageVoice)} controls className="h-5 w-24" />
                            <button onClick={() => setMessageVoice(null)}><X className="w-3 h-3 text-red-400" /></button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      {/* Image picker */}
                      <input ref={fileInputRefTeam} type="file" accept="image/*" className="hidden" onChange={handleTeamImagePick} />
                      <button onClick={() => fileInputRefTeam.current?.click()} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Image className="w-3 h-3" /></button>
                      {/* Camera capture */}
                      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleTeamImagePick} />
                      <button onClick={() => cameraInputRef.current?.click()} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Camera className="w-3 h-3" /></button>
                      {/* Voice recording */}
                      {recordingTeam ? (
                        <button onClick={stopTeamRecording} className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse"><Square className="w-3 h-3" /></button>
                      ) : (
                        <button onClick={startTeamRecording} className="w-8 h-8 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center flex-shrink-0"><Mic className="w-3 h-3" /></button>
                      )}
                      {/* Text input */}
                      <textarea
                        value={convReply}
                        onChange={e => setConvReply(e.target.value)}
                        placeholder={`Répondre à ${selectedConversation.name}...`}
                        rows={1}
                        className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none resize-none"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleConvSend(); } }}
                      />
                      <button
                        onClick={handleConvSend}
                        disabled={(!convReply.trim() && !messageImage && !messageVoice) || convSending}
                        className="w-10 h-10 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl flex items-center justify-center flex-shrink-0"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Messages summary — clickable buttons to open each conversation */}
              {!selectedConversation && (() => {
                const unreadContacts = allContacts.filter(c => getUnreadFrom({ name: c.name, email: c.email }) > 0);
                // Also show contacts with messages (even if read) so user can always click
                const contactsWithMessages = allContacts.filter(c =>
                  messages.some(m =>
                    // Incoming or outgoing message involving this contact
                    ((m.teamId === currentTeam.id || m.recipientTeamId === currentTeam.id) &&
                     (m.senderName || '').includes(c.name)) ||
                    (m.fromTeam && m.senderName === currentTeam.name && m.recipientTeamId === c.id)
                  )
                );
                // Merge: unread first, then rest
                const shownContacts = [...unreadContacts];
                for (const c of contactsWithMessages) {
                  if (!shownContacts.some(x => x.id === c.id)) shownContacts.push(c);
                }
                if (shownContacts.length === 0 && myMessages.length === 0) return null;
                return (
                  <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/50 rounded-2xl p-4 space-y-2">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest text-center">
                      {unreadContacts.length > 0
                        ? `${unreadMessages.length} message(s) non lu(s)`
                        : `${myMessages.length} message(s) reçu(s)`}
                    </p>
                    <div className="space-y-1.5">
                      {shownContacts.map(c => {
                        const isUnread = getUnreadFrom({ name: c.name, email: c.email }) > 0;
                        return (
                        <button
                          key={c.id}
                          onClick={() => openConversation({ name: c.name, id: c.id, email: c.email })}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all group ${
                            isUnread
                              ? 'bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 hover:border-indigo-500/40'
                              : 'bg-[#1A1D23]/40 hover:bg-[#1A1D23] border border-[#2D3139]/30 hover:border-[#2D3139]'
                          }`}
                        >
                          {c.photo ? (
                            <img src={c.photo} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-indigo-400">{c.name.charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-semibold text-white truncate block">
                              {findBestTeamByEmail(team, c.email)?.name || c.name}
                            </span>
                            <span className="text-[10px] text-slate-500 truncate block">
                              {roleLabel(c)}
                              {c.secondaryRoles.length > 0 && <span className="text-slate-600"> · {c.secondaryRoles.join(', ')}</span>}
                              {c.orgName ? ` · ${c.orgName}` : ''}
                            </span>
                          </div>
                          {isUnread && (
                            <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 animate-orange-blink" />
                          )}
                        </button>
                      )})}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === 'external_missions' && (
          <div className="h-full overflow-y-auto pt-12 pb-32 px-5 sm:p-5 sm:pb-32 space-y-6 custom-scrollbar relative z-10" style={{ overscrollBehaviorY: 'contain' }}>
            <div className="w-full max-w-4xl mx-auto space-y-6 animate-fadeIn">
              {/* Header Card */}
              <div className="bg-gradient-to-r from-indigo-950/60 to-purple-950/40 border border-indigo-500/20 rounded-3xl p-6 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <h2 className="text-3xl font-black text-white flex items-center mb-2 animate-fadeIn">
                  {currentTeam?.isArtist ? (
                    <>
                      <User className="mr-3 w-8 h-8 text-amber-400 animate-pulse" /> Mon Profil Artiste 👑
                    </>
                  ) : (
                    <>
                      <Globe className="mr-3 w-8 h-8 text-indigo-400 animate-spin-slow" /> Espace Freelance 🌐
                    </>
                  )}
                </h2>
                <p className="text-slate-300 text-sm leading-relaxed max-w-2xl">
                  {currentTeam?.isArtist 
                    ? "Gérez les informations de votre profil d'artiste, vos styles musicaux, votre cachet indicatif et restez visible pour les organisateurs d'événements."
                    : "Rendez votre profil visible aux autres organisations pour recevoir des propositions de missions directes, négocier vos tarifs et gérer vos prestations en toute autonomie."
                  }
                </p>
              </div>

              {!currentTeam?.emailConfirmed ? (
                /* ── EMAIL VERIFICATION STEP ── */
                <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/80 rounded-3xl p-6 sm:p-8 shadow-2xl max-w-lg mx-auto space-y-6">
                  <div className="text-center space-y-2">
                    <div className="w-16 h-16 bg-indigo-500/10 rounded-2xl border border-indigo-500/20 flex items-center justify-center mx-auto text-indigo-400">
                      <Mail className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Validation de votre adresse e-mail</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">
                      Pour activer la visibilité de votre profil, veuillez confirmer votre adresse e-mail. Un code de validation à 6 chiffres vous sera envoyé.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Votre adresse e-mail</label>
                      <input 
                        type="email" 
                        disabled={codeSent}
                        value={tempEmail}
                        onChange={(e) => setTempEmail(e.target.value)}
                        placeholder="nom@exemple.com"
                        className="w-full bg-[#0A0B0E] border border-[#2D3139] disabled:opacity-50 rounded-xl px-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-colors"
                      />
                    </div>

                    {codeSent ? (
                      <div className="space-y-4 animate-fadeIn">
                        <div>
                          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Code de confirmation (6 chiffres)</label>
                          <input 
                            type="text" 
                            maxLength={6}
                            value={verificationCodeInput}
                            onChange={(e) => setVerificationCodeInput(e.target.value.replace(/\D/g, ''))}
                            placeholder="123456"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-center text-lg font-black tracking-widest text-white focus:border-indigo-500 focus:outline-none transition-colors"
                          />
                        </div>
                        {verificationError && (
                          <p className="text-xs text-red-400 font-bold bg-red-500/10 p-3 rounded-lg border border-red-500/20">{verificationError}</p>
                        )}
                        <div className="flex gap-3">
                          <button 
                            onClick={() => setCodeSent(false)}
                            className="flex-1 py-3 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] rounded-xl text-xs font-black text-slate-300 uppercase tracking-widest transition-all"
                          >
                            Retour
                          </button>
                          <button 
                            onClick={handleVerifyCode}
                            disabled={verificationCodeInput.length !== 6}
                            className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-xl text-xs font-black text-white uppercase tracking-widest shadow-lg shadow-indigo-600/30 transition-all"
                          >
                            Valider mon mail
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button 
                        onClick={handleSendVerificationCode}
                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-black text-white uppercase tracking-widest shadow-lg shadow-indigo-600/30 transition-all flex items-center justify-center gap-2"
                      >
                        <ShieldCheck className="w-4 h-4" /> Envoyer le code de validation
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* ── FREELANCE ACTIVE HUB ── */
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  
                  {/* LEFT COLUMN: PROFILE CONFIGURATION */}
                  <div className="md:col-span-5 space-y-6">
                    
                    {/* Visibility Toggle */}
                    <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/80 rounded-3xl p-5 shadow-xl flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${currentTeam?.isProfileVisible ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                          <Globe className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-black text-white uppercase tracking-wider">Visibilité du Profil</p>
                          <p className="text-[10px] text-slate-400">{currentTeam?.isProfileVisible ? 'Profil public visible' : 'Profil privé masqué'}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => updateTeam(currentTeam.id, { isProfileVisible: !currentTeam.isProfileVisible })}
                        className={`w-12 h-6 rounded-full p-1 transition-all duration-300 ${currentTeam?.isProfileVisible ? 'bg-indigo-600 flex justify-end' : 'bg-slate-700 flex justify-start'}`}
                      >
                        <span className="w-4 h-4 bg-white rounded-full shadow-md" />
                      </button>
                    </div>

                    {/* Email notifications toggle */}
                    <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/80 rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${!currentTeam?.disableMessageEmails ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                          <Mail className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-black text-white uppercase tracking-wider">Notifications Email</p>
                          <p className="text-[10px] text-slate-400">{!currentTeam?.disableMessageEmails ? 'Emails activés pour les messages' : 'Emails désactivés pour les messages'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => updateTeam(currentTeam.id, { disableMessageEmails: !currentTeam.disableMessageEmails })}
                        className={`w-12 h-6 rounded-full p-1 transition-all duration-300 ${!currentTeam?.disableMessageEmails ? 'bg-indigo-600 flex justify-end' : 'bg-slate-700 flex justify-start'}`}
                      >
                        <span className="w-4 h-4 bg-white rounded-full shadow-md" />
                      </button>
                    </div>

                    {/* Profile editor */}
                    <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/80 rounded-3xl p-6 shadow-xl space-y-5">
                      <h3 className="font-bold text-white text-base border-b border-[#2D3139]/50 pb-2.5 flex items-center gap-2 animate-fadeIn">
                        <User className="w-4.5 h-4.5 text-indigo-400" /> {currentTeam?.isArtist ? "Mon Profil Artiste" : "Mon Profil Freelance"}
                      </h3>

                      <div className="space-y-4">
                        {/* Zone Géo */}
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Zone Géographique</label>
                          <input 
                            type="text" 
                            value={zoneInput}
                            onChange={(e) => setZoneInput(e.target.value)}
                            placeholder="Tunis, Sousse, Europe, etc."
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-indigo-500 focus:outline-none transition-colors"
                          />
                        </div>

                        {/* Tarif journalier indicatif */}
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Tarif Journalier Indicatif</label>
                          <div className="flex gap-2">
                            <input 
                              type="number" 
                              value={rateInput || ''}
                              onChange={(e) => setRateInput(parseFloat(e.target.value) || 0)}
                              placeholder="Tarif"
                              className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3.5 py-2.5 text-xs text-white focus:border-indigo-500 focus:outline-none transition-colors"
                            />
                            <select 
                              value={currencyInput}
                              onChange={(e) => setCurrencyInput(e.target.value)}
                              className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none transition-colors"
                            >
                              <option value="TND">TND</option>
                              <option value="EUR">EUR</option>
                              <option value="USD">USD</option>
                            </select>
                          </div>
                        </div>

                        {/* Compétences tags */}
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">Compétences & Spécialités</label>
                          <div className="flex flex-wrap gap-1.5 mb-2.5">
                            {['Bar', 'Cashless', 'Accueil', 'Logistique', 'Électricité', 'Son & Lumière', 'Sécurité'].map(skill => {
                              const isSelected = (currentTeam?.publicSkills || []).includes(skill);
                              return (
                                <button 
                                  key={skill}
                                  onClick={() => toggleSkill(skill)}
                                  className={`text-[9px] font-bold px-2 py-1 rounded-full border transition-all ${
                                    isSelected 
                                      ? 'bg-indigo-500/25 border-indigo-500/50 text-indigo-300' 
                                      : 'bg-white/5 border-white/5 text-slate-400 hover:border-white/10'
                                  }`}
                                >
                                  {skill}
                                </button>
                              );
                            })}
                          </div>

                          {/* Custom Skills List & Input */}
                          <div className="flex gap-1.5">
                            <input 
                              type="text" 
                              value={customSkillInput}
                              onChange={(e) => setCustomSkillInput(e.target.value)}
                              placeholder="Autre spécialité..."
                              className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none transition-colors"
                            />
                            <button 
                              onClick={handleAddCustomSkill}
                              className="bg-indigo-600 hover:bg-indigo-500 p-2 rounded-xl text-white border border-indigo-500 transition-all flex items-center justify-center"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Render custom user added skills that aren't in defaults */}
                          {(() => {
                            const customSkills = (currentTeam?.publicSkills || []).filter(
                              s => !['Bar', 'Cashless', 'Accueil', 'Logistique', 'Électricité', 'Son & Lumière', 'Sécurité'].includes(s)
                            );
                            if (customSkills.length === 0) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {customSkills.map(skill => (
                                  <span key={skill} className="inline-flex items-center gap-1 bg-purple-500/10 border border-purple-500/25 text-purple-300 text-[9px] font-semibold px-2 py-0.5 rounded-full">
                                    {skill}
                                    <button onClick={() => toggleSkill(skill)} className="hover:text-white bg-purple-500/20 rounded-full w-3.5 h-3.5 flex items-center justify-center font-black">&times;</button>
                                  </span>
                                ))}
                                
    </div>
  );
})()}
                        </div>

                        {/* Description / Bio */}
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Biographie / Description</label>
                          <textarea 
                            value={descriptionInput}
                            onChange={(e) => setDescriptionInput(e.target.value)}
                            placeholder="Présentez vos services, disponibilités, expérience..."
                            rows={3}
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-3 text-xs text-white focus:border-indigo-500 focus:outline-none transition-colors resize-none"
                          />
                        </div>

                        <button 
                          onClick={handleSaveProfile}
                          className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-xl text-xs font-black text-white uppercase tracking-widest shadow-md shadow-indigo-600/20 transition-all flex items-center justify-center gap-2"
                        >
                          Enregistrer mon profil 💾
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT COLUMN: EXTERNAL PROPOSALS */}
                  <div className="md:col-span-7 space-y-6">
                    <div className="bg-[#14161B]/80 backdrop-blur-md border border-[#2D3139]/80 rounded-3xl p-6 shadow-xl space-y-5">
                      <h3 className="font-bold text-white text-base border-b border-[#2D3139]/50 pb-2.5 flex items-center gap-2">
                        <ClipboardList className="w-4.5 h-4.5 text-purple-400" /> Missions & Prestations Proposées
                      </h3>

                      {(() => {
                        const extShifts = shifts.filter(
                          s => s.teamId === currentTeam?.id && s.orgId !== currentTeam?.orgId
                        );

                        if (extShifts.length === 0) {
                          return (
                            <div className="text-center py-12 space-y-3">
                              <div className="w-12 h-12 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto text-slate-500 border border-[#2D3139]/40">
                                <Clock className="w-5 h-5" />
                              </div>
                              <p className="text-slate-400 text-xs italic">Aucune proposition de mission externe pour le moment.</p>
                              <p className="text-[10px] text-slate-500 px-6 max-w-sm mx-auto">
                                Dès qu'une organisation externe vous planifie, sa proposition apparaîtra ici avec son offre de tarif journalier.
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            {extShifts.map(sh => {
                              const org = organizations.find(o => o.id === sh.orgId);
                              const pt = points.find(p => p.id === sh.pointId);
                              const orgName = org ? org.name : 'Organisation Externe';
                              const ptName = pt ? pt.name : 'Point Opérationnel';
                              
                              const status = sh.agreementStatus || 'pending_agreement';
                              const isNegotiating = negotiatingShiftId === sh.id;

                              return (
                                <div key={sh.id} className="bg-[#0A0B0E]/70 border border-[#2D3139]/60 rounded-2xl p-4 space-y-3 hover:border-indigo-500/30 transition-all">
                                  <div className="flex justify-between items-start flex-wrap gap-2">
                                    <div>
                                      <span className="text-[9px] font-black text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20 uppercase tracking-widest">{orgName}</span>
                                      <h4 className="font-bold text-white text-sm mt-1">{ptName}</h4>
                                      <p className="text-[10px] text-slate-400">
                                        {new Date(sh.start).toLocaleString('fr-FR', {weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'})} - {new Date(sh.end).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      {status === 'pending_agreement' && (
                                        <span className="inline-block bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider animate-pulse">En attente ⏳</span>
                                      )}
                                      {status === 'negotiated' && (
                                        <span className="inline-block bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider">Négociation 💸</span>
                                      )}
                                      {(status === 'agreed' || status === 'approved') && (
                                        <span className="inline-block bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider">Confirmé ✅</span>
                                      )}
                                      {status === 'rejected' && (
                                        <span className="inline-block bg-red-500/10 text-red-400 border border-red-500/20 text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider">Décliné ❌</span>
                                      )}
                                      <div className="text-xs font-bold text-slate-200 mt-1">
                                        Tarif : {sh.proposedAmount || 0} {sh.proposedCurrency || 'TND'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action block based on status */}
                                  {status === 'pending_agreement' && (
                                    <div className="pt-2 border-t border-[#2D3139]/40 flex flex-col gap-2">
                                      {isNegotiating ? (
                                        <div className="flex gap-2 items-center bg-[#14161B]/60 p-2.5 rounded-xl border border-indigo-500/20 animate-fadeIn">
                                          <input 
                                            type="number" 
                                            value={counterProposedAmount || ''}
                                            onChange={(e) => setCounterProposedAmount(parseFloat(e.target.value) || 0)}
                                            placeholder="Votre prix contre-proposé"
                                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none"
                                          />
                                          <button 
                                            onClick={() => {
                                              updateShift(sh.id, {
                                                proposedAmount: counterProposedAmount,
                                                agreementStatus: 'negotiated',
                                                amountNegotiatedByTeam: true
                                              });
                                              setNegotiatingShiftId(null);
                                              alert("Contre-proposition de prix envoyée à l'organisation ! 💸");
                                            }}
                                            className="bg-indigo-600 hover:bg-indigo-500 px-3 py-2 rounded-xl text-white text-[10px] font-bold uppercase transition-all"
                                          >
                                            Envoyer
                                          </button>
                                          <button 
                                            onClick={() => setNegotiatingShiftId(null)}
                                            className="text-slate-400 hover:text-white px-2 py-1 text-xs"
                                          >
                                            Annuler
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex gap-2 flex-wrap">
                                          <button 
                                            onClick={() => {
                                              updateShift(sh.id, { agreementStatus: 'agreed', agreedAmount: sh.proposedAmount });
                                              alert("Vous avez accepté la mission ! ✅");
                                            }}
                                            className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-[10px] font-black text-white uppercase tracking-wider transition-all"
                                          >
                                            Accepter
                                          </button>
                                          <button 
                                            onClick={() => {
                                              setNegotiatingShiftId(sh.id);
                                              setCounterProposedAmount(sh.proposedAmount || 0);
                                            }}
                                            className="flex-1 py-2 bg-indigo-600/30 hover:bg-indigo-600/40 text-indigo-300 rounded-xl border border-indigo-500/20 text-[10px] font-black uppercase tracking-wider transition-all"
                                          >
                                            Négocier 💸
                                          </button>
                                          <button 
                                            onClick={() => {
                                              if (confirm("Voulez-vous vraiment refuser cette mission ?")) {
                                                updateShift(sh.id, { agreementStatus: 'rejected' });
                                              }
                                            }}
                                            className="flex-1 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-xl border border-red-500/20 text-[10px] font-black uppercase tracking-wider transition-all"
                                          >
                                            Décliner
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {status === 'negotiated' && (
                                    <div className="pt-2 border-t border-[#2D3139]/40 text-[10px] text-slate-400 italic flex items-center gap-1.5">
                                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse"></span>
                                      En attente d'approbation de votre tarif par l'organisation...
                                    </div>
                                  )}

                                  {(status === 'agreed' || status === 'approved') && (
                                    <div className="pt-2 border-t border-[#2D3139]/40 text-[10px] text-emerald-400 font-bold flex items-center gap-1.5">
                                      <CheckCircle className="w-4 h-4" /> Prestation confirmée avec succès ! Bon shift !
                                    </div>
                                  )}

                                  {status === 'rejected' && (
                                    <div className="pt-2 border-t border-[#2D3139]/40 text-[10px] text-red-400 font-bold flex items-center gap-1.5">
                                      <XCircle className="w-4 h-4" /> Shift refusé ou annulé.
                                    </div>
                                  )}
                                  
    </div>
  );
})}
                            
    </div>
  );
})()}
                    </div>
                  </div>

                </div>
              )}
            </div>
          </div>
        )}
      </div>



      {/* Scanner fullscreen overlay modal */}
      {scannerOpen && (
        <div className="fixed inset-0 bg-[#0A0B0E]/95 backdrop-blur-sm z-[55] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-[#2D3139]">
            <span className="font-bold text-white text-lg">Scanner QR Code</span>
            <button onClick={() => { setScannerOpen(false); setScanError(''); }} className="p-2 bg-[#1A1D23] rounded-xl text-slate-400 hover:text-white border border-[#2D3139]">✕</button>
          </div>
          <div className="flex-1 relative">
            <Scanner onScan={(result) => result && handleScan(result[0].rawValue)} onError={() => setScanError("Erreur d'accès à la caméra.")} />
          </div>
          {scanError && <p className="text-red-400 text-sm text-center p-4 bg-red-500/10 font-medium">{scanError}</p>}
        </div>
      )}

      {/* Wallet QR scanner modal */}
      {walletScannerOpen && (
        <div className="fixed inset-0 bg-[#0A0B0E]/95 backdrop-blur-sm z-[56] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-[#2D3139]">
            <span className="font-bold text-white text-lg">
              {walletReplaceMode ? 'Scanner nouveau QR Code' : 'Link My Wallet'}
            </span>
            <button onClick={() => { setWalletScannerOpen(false); setWalletReplaceMode(false); }} className="p-2 bg-[#1A1D23] rounded-xl text-slate-400 hover:text-white border border-[#2D3139]">✕</button>
          </div>
          <div className="flex-1 relative">
            {walletReplaceMode && (
              <div className="absolute top-3 left-3 right-3 z-10 bg-amber-500/20 border border-amber-500/40 rounded-xl p-3 text-center">
                <p className="text-[11px] text-amber-300 font-bold">
                  ⚠️ Scannez le nouveau QR code pour remplacer l'ancien
                </p>
              </div>
            )}
            <Scanner onScan={(result) => result && handleWalletScan(result[0].rawValue)} onError={(error) => console.error('Wallet scanner error:', error)} />
          </div>
          <div className="p-4 border-t border-[#2D3139]">
            <p className="text-[9px] text-slate-500 text-center">
              Placez le QR code dans le cadre pour le scanner
            </p>
          </div>
        </div>
      )}

      {/* Bottom Navigation — flex-none, part of layout flow */}
      <div className="flex-none bg-[#14161B]/95 backdrop-blur-xl border-t border-[#2D3139]/50 shadow-[0_-10px_30px_rgba(0,0,0,0.5)]">
        <div className="flex max-w-md mx-auto relative px-2">
          {/* Active Tab Indicator */}
          {(() => {
            const tabs: ('map' | 'timeline' | 'messages' | 'badge' | 'external_missions' | 'swaps')[] = ['map', 'timeline', 'messages', 'badge', 'external_missions'];
            if (swapAllowedOnAnyShift) {
              tabs.push('swaps');
            }
            const activeIndex = tabs.indexOf(activeTab);
            const tabWidth = 100 / tabs.length;
            return (
              <>
                <div className="absolute top-0 h-[2px] bg-indigo-500 transition-all duration-300"
                  style={{ width: `${tabWidth}%`, left: `${activeIndex * tabWidth}%` }}
                />
                
                <button onClick={() => setActiveTab('map')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'map' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                  <Map className="w-6 h-6" />
                  <span className="text-[9px] uppercase tracking-widest font-bold">Live Map</span>
                </button>
                <button onClick={() => setActiveTab('timeline')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'timeline' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                  <Clock className="w-6 h-6" />
                  <span className="text-[9px] uppercase tracking-widest font-bold">Timeline</span>
                </button>
                <button onClick={() => setActiveTab('messages')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'messages' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                  <div className="relative">
                    <Bell className="w-6 h-6" />
                    {unreadMessages.length > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-orange-blink border-[2px] border-[#14161B]"></span>}
                  </div>
                  <span className="text-[9px] uppercase tracking-widest font-bold">Messages</span>
                </button>
                <button onClick={() => setActiveTab('badge')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'badge' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                  <User className="w-6 h-6" />
                  <span className="text-[9px] uppercase tracking-widest font-bold">Badge</span>
                </button>
                <button onClick={() => setActiveTab('external_missions')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'external_missions' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                  <Globe className="w-6 h-6" />
                  <span className="text-[9px] uppercase tracking-widest font-bold">
                    {currentTeam?.isArtist ? 'Profil' : 'Freelance'}
                  </span>
                </button>
                {swapAllowedOnAnyShift && (
                  <button onClick={() => setActiveTab('swaps')} className={`flex-1 flex flex-col items-center justify-center py-4 space-y-1.5 transition-all ${activeTab === 'swaps' ? 'text-indigo-400 -translate-y-1' : 'text-slate-500 hover:text-slate-300'}`}>
                    <div className="relative">
                      <RefreshCw className="w-6 h-6" />
                      {swapableShifts.length > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-[2px] border-[#14161B]"></span>}
                    </div>
                    <span className="text-[9px] uppercase tracking-widest font-bold">Échanges</span>
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Incident Reporting Modal */}
      {issueModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60] backdrop-blur-sm">
          <div className="bg-[#14161B] border border-red-500/30 rounded-2xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl shadow-red-500/5">
            <div className="p-4 border-b border-[#2D3139] flex justify-between items-center bg-[#1A1D23]">
              <h3 className="font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500 animate-pulse" />
                Signaler un Incident
              </h3>
              <button 
                onClick={() => { setIssueModalOpen(false); setIssueMessage(''); }} 
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-[#2D3139] transition-all"
              >
                ✕
              </button>
            </div>
            
            <div className="p-5 space-y-4 bg-[#0A0B0E]/95">
              {/* Point Selection */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Lieu de l'incident</label>
                <select
                  value={issuePointId}
                  onChange={e => setIssuePointId(e.target.value)}
                  className="w-full bg-[#14161B] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="" disabled>Choisir un point opérationnel...</option>
                  {points.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Visibility Choice */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Type de signalement</label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setIssueType('incident_public')}
                    className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all ${
                      issueType === 'incident_public'
                        ? 'bg-red-500/10 border-red-500/40 text-red-300'
                        : 'bg-[#14161B] border-[#2D3139] text-slate-400 hover:text-white'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center mt-0.5 ${
                      issueType === 'incident_public' ? 'border-red-500' : 'border-slate-600'
                    }`}>
                      {issueType === 'incident_public' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold">🌐 Incident Opérationnel</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Clignote en rouge sur la carte publique pour tout le monde.</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIssueType('incident_internal')}
                    className={`p-3 rounded-xl border text-left flex items-start gap-2.5 transition-all ${
                      issueType === 'incident_internal'
                        ? 'bg-indigo-500/10 border-indigo-500/40 text-indigo-300'
                        : 'bg-[#14161B] border-[#2D3139] text-slate-400 hover:text-white'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center mt-0.5 ${
                      issueType === 'incident_internal' ? 'border-indigo-500' : 'border-slate-600'
                    }`}>
                      {issueType === 'incident_internal' && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold">🔒 Problème Interne</span>
                      <span className="text-[10px] text-slate-500 mt-0.5">Confidentiel. Visible uniquement par les managers et l'admin.</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Message Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description du problème</label>
                <textarea
                  value={issueMessage}
                  onChange={e => setIssueMessage(e.target.value)}
                  placeholder="Décrivez précisément ce qui se passe..."
                  rows={3}
                  className="w-full bg-[#14161B] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-red-500 focus:outline-none resize-none"
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-[#2D3139] flex gap-3 bg-[#1A1D23]">
              <button
                onClick={() => { setIssueModalOpen(false); setIssueMessage(''); }}
                className="flex-1 py-2.5 bg-[#14161B] hover:bg-[#22262E] text-slate-300 hover:text-white rounded-xl text-sm transition-all border border-[#2D3139]"
              >
                Annuler
              </button>
              <button
                onClick={submitIssue}
                disabled={!issueMessage.trim() || !issuePointId}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:pointer-events-none text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-red-500/20"
              >
                🚨 Signaler
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]">
          <div className="bg-[#14161B] border border-[#2D3139] p-6 rounded-xl w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-bold text-white mb-2">Confirmation</h3>
              <p className="text-sm text-slate-400 mb-6">{confirmAction.message}</p>
              <div className="flex space-x-3">
                <button onClick={() => setConfirmAction(null)} className="flex-1 py-2 bg-[#1A1D23] hover:bg-[#22262E] text-white rounded-lg transition-colors text-sm">Annuler</button>
                <button onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }} className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors text-sm font-medium">Confirmer</button>
              </div>
          </div>
        </div>
      )}

      {/* 📲 DISTRIBUTOR SCAN & CASHIER CONSOLE MODAL */}
      {distributorScannerOpen && (
        <div className="fixed inset-0 bg-[#0A0B0E]/95 backdrop-blur-md z-[55] flex flex-col overflow-hidden text-slate-100">
          {/* Header */}
          <div className="flex-none flex items-center justify-between p-4 border-b border-[#2D3139] bg-[#14161B]">
            <div className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-400 animate-pulse" />
              <span className="font-black text-white text-base tracking-tight">Console Distributeur</span>
            </div>
            <button 
              onClick={() => {
                setDistributorScannerOpen(false);
                setScannedTeamId(null);
                setBasket({});
                setDistributorScanError('');
                setDistributorUnlinkedQr(null);
                setDistributorLinkTarget(null);
                setDistributorSuccessMsg('');
              }}
              className="p-2 bg-[#1A1D23] hover:bg-red-500/10 hover:text-red-400 rounded-xl text-slate-400 border border-[#2D3139] transition-all"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-5 flex flex-col items-center" style={{ overscrollBehaviorY: 'contain' }}>
            <div className="w-full max-w-md space-y-5">
              
              {/* Point of distribution selector (toggle-sticky) */}
              <div className="bg-[#14161B] border border-[#2D3139]/80 rounded-2xl p-4 shadow-xl">
                <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Lieu de distribution actif</label>
                <select
                  value={distributorPointId}
                  onChange={(e) => setDistributorPointId(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-xs text-white focus:border-indigo-500 focus:outline-none font-bold"
                >
                  <option value="">Aucun point sélectionné</option>
                  {points.filter(p => (myOrgIds.includes(p.orgId || ''))).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Success message */}
              {distributorSuccessMsg && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-2xl text-center space-y-1 animate-in fade-in">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="text-xs font-black text-emerald-300 uppercase">Succès</h4>
                  </div>
                  <p className="text-[10px] text-emerald-200/80 leading-normal">{distributorSuccessMsg}</p>
                  <button
                    onClick={() => setDistributorSuccessMsg('')}
                    className="text-[9px] text-emerald-400 hover:text-emerald-300 underline mt-1"
                  >
                    Fermer
                  </button>
                </div>
              )}

              {/* No Team selected: Scan or Search */}
              {!scannedTeamId ? (
                <div className="space-y-4">
                  {/* Selector tabs — only show tabs whose permission is granted */}
                  <div className="flex bg-[#14161B] p-1 rounded-xl border border-[#2D3139]">
                    {(currentTeam?.distAllowScan || currentTeam?.distAllowLinkWallet) && (
                      <button
                        onClick={() => setDistTabSub('scan')}
                        className={`flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${distTabSub === 'scan' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                      >
                        Scanner QR Code
                      </button>
                    )}
                    {(currentTeam?.isDistributor && currentTeam?.distAllowDirect) && (
                      <button
                        onClick={() => setDistTabSub('search')}
                        className={`flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${distTabSub === 'search' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                      >
                        Saisie Directe
                      </button>
                    )}
                    {currentTeam?.distAllowView && (
                      <button
                        onClick={() => setDistTabSub('list')}
                        className={`flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${distTabSub === 'list' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                      >
                        Liste
                      </button>
                    )}
                  </div>

                  {distTabSub === 'scan' && (
                    <div className="space-y-4">
                      {/* Flow B: List→Scan — Link wallet to a specific member */}
                      {distributorLinkTarget ? (
                        (() => {
                          const linkMember = team.find(s => s.id === distributorLinkTarget);
                          return (
                            <div className="space-y-3">
                              <div className="bg-cyan-500/10 border border-cyan-500/30 p-4 rounded-2xl text-center space-y-2">
                                <Link className="w-7 h-7 text-cyan-400 mx-auto" />
                                <h4 className="text-xs font-black text-cyan-300 uppercase">Lier un Wallet QR</h4>
                                <p className="text-[10px] text-slate-400 leading-normal">
                                  Scannez le QR code wallet à associer à <strong className="text-white">{linkMember?.name}</strong>
                                </p>
                              </div>
                              <div className="relative rounded-2xl overflow-hidden border border-[#2D3139] bg-black max-w-[320px] mx-auto shadow-2xl">
                                <Scanner
                                  onScan={(result) => result && handleDistributorScan(result[0].rawValue)}
                                  onError={() => setDistributorScanError("Erreur d'accès à la caméra.")}
                                />
                              </div>
                              <button
                                onClick={handleCancelLinkWallet}
                                className="w-full py-2 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/30 text-slate-400 font-bold text-[10px] uppercase tracking-wider transition-all"
                              >
                                Annuler
                              </button>
                            </div>
                          );
                        })()
                      ) : distributorUnlinkedQr ? (
                        /* Flow A: Scan→Link — QR scanned but not linked yet */
                        <div className="space-y-3">
                          <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-2xl text-center space-y-2">
                            <Link className="w-7 h-7 text-amber-400 mx-auto" />
                            <h4 className="text-xs font-black text-amber-300 uppercase">QR Code Non Reconnu</h4>
                            <p className="text-[10px] text-slate-400 leading-normal">
                              Ce QR n'est lié à aucun membre. Choisissez un membre ci-dessous pour le lier.
                            </p>
                            <p className="text-[9px] font-mono text-amber-400/70 break-all bg-[#0A0B0E] rounded-lg p-2 border border-amber-500/20">
                              {distributorUnlinkedQr}
                            </p>
                          </div>
                          <div className="relative">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                            <input
                              type="text"
                              placeholder="Nom du membre à lier..."
                              value={distributorSearchQuery}
                              onChange={(e) => setDistributorSearchQuery(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded-2xl pl-10 pr-4 py-2.5 text-xs text-white focus:border-amber-500 focus:outline-none"
                            />
                          </div>
                          <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                            {team
                              .filter(s => myOrgIds.includes(s.orgId || '') && (s.name.toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (s.email || '').toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (cfValuesByTeam[s.id] || '').includes(distributorSearchQuery.toLowerCase())))
                              .map(s => (
                                <button
                                  key={s.id}
                                  onClick={() => handleLinkWalletToMember(s.id)}
                                  className="w-full p-3 bg-[#14161B] hover:bg-amber-500/10 border border-[#2D3139]/80 hover:border-amber-500/30 rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between group active:scale-[0.99]"
                                >
                                  <span>{s.name}</span>
                                  <span className="text-[9px] text-amber-400 group-hover:text-amber-300 uppercase tracking-widest flex items-center gap-1">
                                    <Link className="w-3 h-3" /> Lier ➔
                                  </span>
                                </button>
                              ))}
                          </div>
                          <button
                            onClick={handleCancelLinkWallet}
                            className="w-full py-2 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/30 text-slate-400 font-bold text-[10px] uppercase tracking-wider transition-all"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : currentTeam.distAllowScan === false && !currentTeam.distAllowLinkWallet ? (
                        <div className="bg-rose-500/10 border border-rose-500/30 p-6 rounded-2xl text-center space-y-2">
                          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto" />
                          <h4 className="text-xs font-black text-rose-300 uppercase">Scanner Bloqué</h4>
                          <p className="text-[10px] text-slate-400 leading-normal">Votre profil ne dispose pas du droit de scanner les badges.</p>
                        </div>
                      ) : (
                        <div className="relative rounded-2xl overflow-hidden border border-[#2D3139] bg-black max-w-[320px] mx-auto shadow-2xl">
                          <Scanner
                            onScan={(result) => result && handleDistributorScan(result[0].rawValue)}
                            onError={() => setDistributorScanError("Erreur d'accès à la caméra.")}
                          />
                        </div>
                      )}

                      {distributorScanError && (
                        <p className="text-rose-400 text-xs text-center p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl font-bold">{distributorScanError}</p>
                      )}
                    </div>
                  )}

                  {distTabSub === 'search' && (
                    <div className="space-y-4">
                      {currentTeam.distAllowDirect === false ? (
                        <div className="bg-rose-500/10 border border-rose-500/30 p-6 rounded-2xl text-center space-y-2">
                          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto" />
                          <h4 className="text-xs font-black text-rose-300 uppercase">Saisie Directe Bloquée</h4>
                          <p className="text-[10px] text-slate-400 leading-normal">Votre profil ne dispose pas du droit d'attribuer directement sans scan.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                            <input
                              type="text"
                              placeholder="Nom, e-mail ou champ personnalisé..."
                              value={distributorSearchQuery}
                              onChange={(e) => setDistributorSearchQuery(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded-2xl pl-10 pr-4 py-2.5 text-xs text-white focus:border-indigo-500 focus:outline-none"
                            />
                          </div>

                          <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                            {team
                              .filter(s => myOrgIds.includes(s.orgId || '') && (s.name.toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (s.email || '').toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (cfValuesByTeam[s.id] || '').includes(distributorSearchQuery.toLowerCase())))
                              .map(s => (
                                <button
                                  key={s.id}
                                  onClick={() => {
                                    setScannedTeamId(s.id);
                                    setBasket({});
                                    setDistributorScanError('');
                                  }}
                                  className="w-full p-3 bg-[#14161B] hover:bg-[#1E222B] border border-[#2D3139]/80 rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between group active:scale-[0.99]"
                                >
                                  <span>{s.name}</span>
                                  <span className="text-[9px] text-slate-500 group-hover:text-indigo-400 uppercase tracking-widest">Sélectionner ➔</span>
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {distTabSub === 'list' && (
                    <div className="space-y-4">
                      {currentTeam.distAllowView === false ? (
                        <div className="bg-rose-500/10 border border-rose-500/30 p-6 rounded-2xl text-center space-y-2">
                          <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto" />
                          <h4 className="text-xs font-black text-rose-300 uppercase">Liste Bloquée</h4>
                          <p className="text-[10px] text-slate-400 leading-normal">Votre profil ne dispose pas du droit de consulter la liste.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                            <input
                              type="text"
                              placeholder="Nom, e-mail ou champ personnalisé..."
                              value={distributorSearchQuery}
                              onChange={(e) => setDistributorSearchQuery(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded-2xl pl-10 pr-4 py-2.5 text-xs text-white focus:border-indigo-500 focus:outline-none"
                            />
                          </div>

                          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(100vh - 420px)' }}>
                            {team
                              .filter(s => myOrgIds.includes(s.orgId || '') && (s.name.toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (s.email || '').toLowerCase().includes(distributorSearchQuery.toLowerCase()) || (cfValuesByTeam[s.id] || '').includes(distributorSearchQuery.toLowerCase())))
                              .map(s => (
                                <div
                                  key={s.id}
                                  className="flex items-center gap-2"
                                >
                                  <button
                                    onClick={() => {
                                      setScannedTeamId(s.id);
                                      setBasket({});
                                      setDistributorScanError('');
                                    }}
                                    className="flex-1 p-3 bg-[#14161B] hover:bg-[#1E222B] border border-[#2D3139]/80 rounded-xl text-left text-xs font-bold transition-all flex items-center justify-between group active:scale-[0.99]"
                                  >
                                    <span>{s.name}</span>
                                    <span className="text-[9px] text-slate-500 group-hover:text-cyan-400 uppercase tracking-widest">Consulter ➔</span>
                                  </button>
                                  {currentTeam?.distAllowLinkWallet && (
                                    <button
                                      onClick={() => {
                                        setDistributorLinkTarget(s.id);
                                        setDistTabSub('scan');
                                        setDistributorScanError('');
                                        setDistributorUnlinkedQr(null);
                                        setDistributorSuccessMsg('');
                                      }}
                                      title={s.linkedWalletQr ? `${s.name} a déjà un bracelet lié — cliquer pour remplacer` : `Lier un wallet QR à ${s.name}`}
                                      className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-95 ${
                                        s.linkedWalletQr
                                          ? 'bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40'
                                          : 'bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 hover:border-cyan-500/40'
                                      }`}
                                    >
                                      <Link className={`w-4 h-4 ${s.linkedWalletQr ? 'text-red-400' : 'text-cyan-400'}`} />
                                    </button>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Team member scanned: Cart Basket */
                (() => {
                  const scannedTeam = team.find(s => s.id === scannedTeamId);
                  const lastResetDate = currentOrg?.settings && (currentOrg.settings as any).last_quota_reset
                    ? new Date((currentOrg.settings as any).last_quota_reset)
                    : null;

                  return (
                    <div className="space-y-5">
                      
                      {/* Scanned Team Profile Card */}
                      <div className="bg-indigo-600/10 border border-indigo-500/20 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#0A0B0E] rounded-xl flex items-center justify-center border border-white/10 text-indigo-400 font-black">
                            {scannedTeam?.name.charAt(0)}
                          </div>
                          <div>
                            <h4 className="text-xs font-black text-white">{scannedTeam?.name}</h4>
                            <p className="text-[9px] text-slate-400 uppercase tracking-widest mt-0.5">
                              {scannedTeam?.roles?.join(', ') || scannedTeam?.role || 'Général'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setScannedTeamId(null)}
                          className="text-[10px] text-indigo-400 hover:text-indigo-300 font-bold uppercase"
                        >
                          Changer
                        </button>
                      </div>

                      {/* Mode switch: Consumption vs Check-in */}
                      {(currentTeam?.isDistributor && currentTeam.distAllowCheckin) ? (
                        <div className="flex bg-[#14161B] p-1 rounded-xl border border-[#2D3139]">
                          <button
                            onClick={() => setDistributorMode('consumption')}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${distributorMode === 'consumption' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                          >
                            Consommations
                          </button>
                          <button
                            onClick={() => setDistributorMode('checkin')}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${distributorMode === 'checkin' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                          >
                            Check-in Présence
                          </button>
                        </div>
                      ) : currentTeam.distAllowCheckin ? (
                        <div className="bg-[#14161B] border border-[#2D3139] rounded-xl p-1">
                          <span className="flex-1 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider text-center block bg-cyan-600 text-white">
                            Check-in Présence
                          </span>
                        </div>
                      ) : null}

                      {(!currentTeam?.isDistributor || distributorMode === 'checkin') ? (
                        <div className="space-y-4">
                          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-5 text-center space-y-4 shadow-xl">
                            <UserCheck className="w-10 h-10 text-cyan-400 mx-auto" />
                            <div>
                              <h4 className="text-xs font-black text-white uppercase tracking-wider">Validation de présence</h4>
                              <p className="text-[10px] text-slate-400 leading-normal mt-1">Marquer ce membre comme présent au lieu de distribution sélectionné.</p>
                            </div>
                            <button
                              onClick={handleConfirmCheckin}
                              disabled={!distributorPointId}
                              className="w-full py-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:pointer-events-none text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-lg shadow-cyan-600/20 active:scale-95 transition-all"
                            >
                              Valider le Check-in 📍
                            </button>
                          </div>

                          {/* ── Check-in History ── */}
                          {(() => {
                            const memberCheckins = checkins
                              .filter(c => c.teamId === scannedTeamId && !c.deletedAt)
                              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

                            if (memberCheckins.length === 0) return null;

                            return (
                              <div className={`rounded-2xl p-4 space-y-2 ${memberCheckins.length > 1 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[#14161B] border border-[#2D3139]'}`}>
                                {memberCheckins.length > 1 && (
                                  <div className="flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-xl px-3 py-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                                    <p className="text-[9px] text-amber-300 font-bold leading-tight">
                                      Attention : ce membre a déjà été checké {memberCheckins.length} fois. Vérifiez qu'il ne s'agit pas d'un double scan.
                                    </p>
                                  </div>
                                )}
                                <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Historique des check-ins</h4>
                                <div className="space-y-1.5 max-h-32 overflow-y-auto custom-scrollbar">
                                  {memberCheckins.map((c, i) => {
                                    const checker = team.find(t => t.id === c.scannedBy);
                                    const date = new Date(c.createdAt);
                                    return (
                                      <div key={c.id} className="flex items-center justify-between text-[9px] bg-[#0A0B0E] rounded-lg px-3 py-2 border border-[#2D3139]/50">
                                        <div className="flex items-center gap-2">
                                          <CheckCircle className={`w-3 h-3 ${i === 0 ? 'text-emerald-400' : 'text-slate-600'}`} />
                                          <span className="text-slate-300">
                                            {date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} à {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        </div>
                                        <span className="text-slate-600">
                                          par {checker?.name || 'Inconnu'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        /* Consumption Basket */
                        <div className="space-y-4">
                          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-4 shadow-xl space-y-3">
                            <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Panier Avantages</h4>
                            <div className="divide-y divide-[#2D3139]/40 space-y-3">
                              {offerProducts
                                .filter(p => myOrgIds.includes(p.orgId || ''))
                                .map(prod => {
                                  const totalQuota = getEffectiveQuota(scannedTeamId, prod.name, prod);

                                  const sumConsumed = consumptions
                                    .filter(c => c.teamId === scannedTeamId && c.productName === prod.name && (!lastResetDate || new Date(c.createdAt) >= lastResetDate))
                                    .reduce((sum, c) => sum + c.quantity, 0);

                                  const remaining = Math.max(0, totalQuota - sumConsumed);
                                  const selectedQty = basket[prod.name] || 0;
                                  const isExceeded = selectedQty > remaining;

                                  return (
                                    <div key={prod.id} className={`pt-3 flex items-center justify-between gap-4 transition-all ${isExceeded ? 'bg-red-500/5 -mx-4 px-4 py-2 border-y border-red-500/20' : ''}`}>
                                      <div>
                                        <h5 className="text-xs font-bold text-white flex items-center gap-1.5">
                                          <span className="w-2 h-2 rounded-full bg-indigo-500" />
                                          {prod.name}
                                        </h5>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className={`text-[10px] font-mono ${remaining > 0 ? 'text-slate-400' : 'text-rose-500 font-bold'}`}>
                                            Quota restant : {remaining} / {totalQuota}
                                          </span>
                                          {isExceeded && (
                                            <span className="text-[8px] bg-rose-500/10 text-rose-400 border border-rose-500/20 font-black px-1 rounded uppercase tracking-wider animate-pulse">
                                              Quota Épuisé
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-3">
                                        <button
                                          onClick={() => setBasket({ ...basket, [prod.name]: Math.max(0, selectedQty - 1) })}
                                          disabled={selectedQty === 0}
                                          className="w-7 h-7 bg-[#0A0B0E] border border-[#2D3139] hover:bg-[#1E222B] disabled:opacity-30 disabled:pointer-events-none rounded-lg text-white font-bold flex items-center justify-center transition-all"
                                        >
                                          <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <span className={`w-6 text-center text-xs font-black font-mono ${selectedQty > 0 ? (isExceeded ? 'text-rose-400 font-black' : 'text-indigo-400') : 'text-slate-500'}`}>
                                          {selectedQty}
                                        </span>
                                        <button
                                          onClick={() => setBasket({ ...basket, [prod.name]: selectedQty + 1 })}
                                          className="w-7 h-7 bg-[#0A0B0E] border border-[#2D3139] hover:bg-[#1E222B] rounded-lg text-white font-bold flex items-center justify-center transition-all"
                                        >
                                          <Plus className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>

                          {/* Confirm Cart Button */}
                          <button
                            onClick={handleConfirmDistribution}
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl text-xs uppercase tracking-widest shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
                          >
                            Confirmer la commande (Panier) 🎁
                          </button>
                        </div>
                      )}
                      
    </div>
  );
})()
              )}
            </div>
          </div>

          {/* ── Custom Buttons Bar (always visible at bottom) ── */}
          {currentTeam?.customButtons && currentTeam.customButtons.length > 0 && (
            <div className="flex-none bg-[#111319] border-t border-[#2D3139] px-4 py-3">
              <div className="flex items-center gap-2 max-w-md mx-auto">
                {currentTeam.customButtons.map((btn, i) => {
                  const colorMap: Record<string, string> = {
                    indigo: 'bg-indigo-600 hover:bg-indigo-500 border-indigo-400/30',
                    emerald: 'bg-emerald-600 hover:bg-emerald-500 border-emerald-400/30',
                    amber: 'bg-amber-600 hover:bg-amber-500 border-amber-400/30',
                    rose: 'bg-rose-600 hover:bg-rose-500 border-rose-400/30',
                    sky: 'bg-sky-600 hover:bg-sky-500 border-sky-400/30',
                    purple: 'bg-purple-600 hover:bg-purple-500 border-purple-400/30',
                  };
                  const colorClass = colorMap[btn.color] || colorMap.indigo;
                  return (
                    <button
                      key={i}
                      onClick={() => window.open(btn.url, '_blank')}
                      className={`flex-1 py-2.5 ${colorClass} text-white font-bold rounded-xl text-[10px] uppercase tracking-wider transition-all border flex items-center justify-center gap-1.5 active:scale-95`}
                    >
                      {btn.icon && <DynamicIcon name={btn.icon} />}
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
            )}

    </div>
  );
}

// ─── Dynamic Icon Helper ────────────────────────────────────────────
function DynamicIcon({ name }: { name: string }) {
  const icons: Record<string, any> = { Link, ExternalLink: Link, Globe, Search, User, Map, Music, Bell, Gift, ClipboardList, Camera, QrCode, ShieldCheck };
  const Icon = icons[name] || Link;
  return <Icon className="w-3.5 h-3.5" />;
}
